import { supabase } from "@/supabase";
import * as mock from "@/views/pulse/_mock";
import { StorageService } from "./storage.service";
import { storage_url } from "../utils/general";

// Resolve a stored avatar path (relative to the brandripplr bucket) to a full
// URL. Already-absolute URLs pass through untouched. null → no photo (initials).
const _avatarUrl = (path) => (path ? (/^https?:\/\//.test(path) ? path : storage_url + path) : null);

// -- Gravatar fallback: hash the email (Gravatar supports SHA-256) and point at
// the avatar with d=404 so the <img> errors out (→ initials) when there's none.
const _gravCache = new Map();
const _sha256hex = async (str) => {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
};
const _gravatarUrl = async (email) => {
  const key = (email || "").trim().toLowerCase();
  if (!key || !key.includes("@")) return null;
  if (_gravCache.has(key)) return _gravCache.get(key);
  try {
    const url = `https://www.gravatar.com/avatar/${await _sha256hex(key)}?d=404&s=200`;
    _gravCache.set(key, url);
    return url;
  } catch { return null; }
};
// Attach a `gravatar` URL to inbound senders that have no explicit photo yet.
const _enrichGravatars = async (threads) => {
  const emails = new Set();
  threads.forEach((t) => {
    if (!t.from.avatar && t.from.email) emails.add(t.from.email.toLowerCase());
    (t.messages || []).forEach((m) => { if (m.dir === "in" && !m.avatar && m.fromEmail) emails.add(m.fromEmail.toLowerCase()); });
  });
  const map = {};
  await Promise.all([...emails].map(async (e) => { map[e] = await _gravatarUrl(e); }));
  threads.forEach((t) => {
    if (!t.from.avatar) t.from.gravatar = map[(t.from.email || "").toLowerCase()] || null;
    (t.messages || []).forEach((m) => { if (m.dir === "in" && !m.avatar) m.gravatar = map[(m.fromEmail || "").toLowerCase()] || null; });
  });
};

// Pulse data layer. Follows the project service convention: each method returns
// a plain object ({ data, error }) with `error` as a message string, so views
// can fall back to mock data or toast on failure.
//
// SCAFFOLD PHASE: reads resolve to mock data. Each method is written so the
// Supabase query can be dropped in later without changing the view contract.
// The pulse_ tables are already deployed; the extended columns land in the
// schema-extend migration (see docs/pulse-build-plan.md).

const ok = (data) => ({ data, error: null });

// -- normalisers: map DB rows → the shapes the Admin view expects ----------
const _fullName = (u) => {
  if (!u) return "Unknown";
  const n = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
  return n || u.email || "Unknown";
};
const _member = (r) => {
  const name = _fullName(r.user);
  return {
    id: r.user_id, name, role: r.user?.email || "",
    initials: mock.initials(name), color: mock.colorFor(name), avatar: _avatarUrl(r.user?.avatar),
    admin: !!r.is_admin,
    // Member's own WhatsApp number — how the listener recognises staff in client groups.
    phone: r.phone || "",
    access: { email: r.email_access, phone: r.phone_access, whatsapp: r.whatsapp_access, calendar: r.calendar_access },
  };
};
const _staff = (u) => {
  const name = _fullName(u);
  return { id: u.id, name, role: u.email || "", initials: mock.initials(name), color: mock.colorFor(name), avatar: _avatarUrl(u.avatar) };
};
const _ORG_NOTE = {
  "Brandripplr": "Company domain — team + shared inboxes.",
  // Key must match the value stored in pulse_channel_account.org (and the option
  // in AdminView's Connect-mailbox dropdown) or the note silently vanishes.
  "Stars Magnet": "Talent-agency domain — the Stars Magnet team (starsmagnetmanagement.com / starsmagnet.com).",
  "Influencer inboxes": "Creator mailboxes the Stars Magnet team manages on their behalf.",
};

const _uid = async () => { const { data } = await supabase.auth.getUser(); return data?.user?.id || null; };

// Decode HTML entities (Gmail snippets/subjects come HTML-encoded, e.g. We&#39;re
// → We're) so they render correctly as plain text in the list/reading pane.
const _decode = (s) => {
  if (!s || s.indexOf("&") === -1) return s || "";
  try { const t = document.createElement("textarea"); t.innerHTML = s; return t.value; }
  catch { return s; }
};

// "Name <email>" | "email" → { name, email }
const _parseAddr = (raw) => {
  const s = (raw || "").trim();
  const m = s.match(/^(.*?)<([^>]+)>$/);
  if (m) return { name: m[1].replace(/["']/g, "").trim(), email: m[2].trim().toLowerCase() };
  return { name: "", email: s.toLowerCase() };
};
// The awaiting flag is deterministic (client wrote last), but not every client
// message needs a reply — "we'll review and get back to you" leaves the ball
// with THEM. If the AI thread-read is FRESH (analyzed after the latest message)
// and says waiting_on is client/none, the thread isn't really awaiting us.
const _aiSaysNoReplyNeeded = (c) => {
  const m = c && c.ai_meta;
  if (!m || !m.analyzed_at || !(m.waiting_on === "client" || m.waiting_on === "none")) return false;
  return new Date(m.analyzed_at) >= new Date(c.last_message_at || 0);
};

const _when = (ts) => {
  if (!ts) return "";
  const d = new Date(ts), now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
                 : d.toLocaleDateString([], { month: "short", day: "numeric" });
};
const _whenFull = (ts) => (ts ? new Date(ts).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
// Message bubbles: time today, otherwise date AND time ("Oct 3, 2:14 PM") —
// a date alone under an older message hides when in the day it was sent.
const _whenMsg = (ts) => {
  if (!ts) return "";
  const d = new Date(ts), now = new Date();
  return d.toDateString() === now.toDateString()
    ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : _whenFull(ts);
};

// Fetch EVERY message for a set of conversations.
//
// PostgREST caps a response at 1000 rows and does it SILENTLY. A plain
// `.in("conversation_id", ids)` across a few hundred threads therefore returned
// only the oldest 1000 messages, so Radar computed reply times, owners and
// promise resolution on a truncated history — the newest messages, i.e. exactly
// the ones that resolve things, were the ones missing. Always page.
const _PAGE = 1000;
const _CONV_CHUNK = 120;   // keep the `in(...)` URL well under length limits
async function _messagesForConversations(ids, select) {
  const out = [];
  for (let i = 0; i < ids.length; i += _CONV_CHUNK) {
    const chunk = ids.slice(i, i + _CONV_CHUNK);
    for (let from = 0; ; from += _PAGE) {
      const { data, error } = await supabase.from("pulse_message")
        .select(select).in("conversation_id", chunk)
        .order("sent_at", { ascending: true }).range(from, from + _PAGE - 1);
      if (error || !data || !data.length) break;
      out.push(...data);
      if (data.length < _PAGE) break;
    }
  }
  return out;
}

// "YYYY-Www" week key (Mon-anchored) for grouping trends.
const _weekKey = (ts) => {
  const d = ts ? new Date(ts) : new Date();
  if (isNaN(d.getTime())) return "";
  const day = (d.getDay() + 6) % 7; // Mon=0
  const monday = new Date(d); monday.setDate(d.getDate() - day);
  return monday.toISOString().slice(0, 10);
};

// Shape a pulse_call row for the Calls screen.
const _fmtDur = (sec) => {
  if (!sec) return "";
  const m = Math.floor(sec / 60), s = sec % 60;
  return m ? `${m}m ${s}s` : `${s}s`;
};
const _mapCall = (r) => {
  const who = _fullName(r.owner) || (r.owner && r.owner.email) || "";
  return {
    id: r.id,
    title: r.title || "Untitled call",
    status: r.status,
    contactName: r.contact_name || "",
    startedAt: r.started_at,
    when: _when(r.started_at),
    whenFull: _whenFull(r.started_at),
    durationSec: r.duration_sec || 0,
    durationLabel: _fmtDur(r.duration_sec),
    transcript: r.transcript || "",
    segments: Array.isArray(r.segments) ? r.segments : [],
    summary: r.summary || "",
    device: r.device || "",
    owner: { name: who, initials: mock.initials(who), color: mock.colorFor(who), avatar: _avatarUrl(r.owner && r.owner.avatar) },
  };
};

// Shape a pulse_conversation (channel='whatsapp') for the Chats list.
const _mapChat = (c) => {
  const name = c.from_name || c.from_address || "WhatsApp";
  const windowOpen = c.wa_window_until ? new Date(c.wa_window_until).getTime() > Date.now() : false;
  return {
    id: c.id,
    channelAccountId: c.channel_account_id || null,
    name,
    initials: mock.initials(name),
    color: mock.colorFor(name),
    avatar: null,
    isGroup: !!c.is_group,
    groupStatus: c.wa_group_status || "",     // pending | active | suspended | deleted | failed | listening | removed
    // A client group the WasenderAPI listener number sits in. Nothing can be
    // sent from Pulse into it — the number is a witness, not a participant.
    readOnly: ["listening", "removed", "imported"].includes(c.wa_group_status),
    groupInviteLink: c.wa_invite_link || "",
    groupDesc: c.wa_group_desc || "",
    groupError: c.wa_group_error || "",
    number: c.from_address || c.external_thread_id || "",
    preview: c.preview || "",
    time: _when(c.last_message_at),
    unread: c.unread_count || 0,
    pinned: false,
    sent: null,
    presence: c.from_address || "",
    windowOpen,
    windowUntil: c.wa_window_until ? _whenFull(c.wa_window_until) : "",
    windowUntilTs: c.wa_window_until || null,
    campaign: "",
    typeLabel: c.wa_group_status === "listening" || c.wa_group_status === "removed"
      ? "Client group · listening"
      : (c.wa_group_status === "imported" ? "Client group · history only" : (c.is_group ? "WhatsApp group" : "WhatsApp contact")),
    ourNumber: "",
    radarMuted: !!c.awaiting_muted,   // group left out of Radar (internal / not a client group)
    // Cached AI summary of the whole chat (same shape as email — pulse-summarize-thread).
    summary: c.summary || null, summaryAt: c.summary_at || null, summaryMsgCount: c.summary_msg_count || null,
    messages: [],
  };
};
const _mapChatMsg = (m) => ({
  id: m.id,
  wamid: m.external_message_id || "",
  dir: m.direction === "outbound" ? "out" : "in",
  type: m.type === "reaction" ? "text" : (m.type || "text"),
  text: m.body || "",
  transcript: m.transcript || "",   // voice/audio speech-to-text (null = pending, "" = nothing said)
  time: _whenMsg(m.sent_at),
  sentAt: m.sent_at || null,   // raw timestamp — drives the day dividers
  ticks: m.delivery_status || "sent",
  deliveryError: m.delivery_error || "",
  // In a group, inbound messages show which participant sent them (the view
  // only renders this for group threads).
  sender: m.direction === "inbound" ? (m.from_name || "") : "",
  senderName: m.from_name || "",
  senderPhone: m.from_address || "",
  // Which staff member sent this outbound message (shared inbox attribution).
  senderStaff: (m.direction === "outbound" && m.sender)
    ? [m.sender.first_name, m.sender.last_name].filter(Boolean).join(" ").trim()
    : "",
  starred: !!m.starred,
  forwarded: !!m.forwarded,
  reactions: Array.isArray(m.reactions) ? m.reactions : [],
  reply: m.reply_to ? { wamid: m.reply_to, snippet: m.reply_snippet || "", dir: m.reply_dir || "in" } : null,
  media: m.media_path ? { path: m.media_path, mime: m.media_mime || "", name: m.media_name || "", duration: m.media_duration || "", url: "" } : null,
  location: m.location || null,
  // Shared contact card(s): [{ name, phones[], org }] (WhatsApp listener stores them in attachments_meta).
  contacts: m.type === "contact" && Array.isArray(m.attachments_meta)
    ? m.attachments_meta.map((c) => ({ name: c.name || "Contact", phones: c.phones || [], org: c.org || "", initials: mock.initials(c.name || "Contact"), color: mock.colorFor(c.name || "Contact") }))
    : null,
  // Poll: { question, options[], multi }. Votes are E2E-encrypted, so no tallies.
  poll: m.type === "poll" && Array.isArray(m.attachments_meta) && m.attachments_meta[0]
    ? { question: m.attachments_meta[0].question || "Poll", options: m.attachments_meta[0].options || [], multi: !!m.attachments_meta[0].multi }
    : null,
  // Link preview: { url, title, description, thumb } captured from WhatsApp's own preview.
  link: m.type === "text" && Array.isArray(m.attachments_meta) && m.attachments_meta[0] && m.attachments_meta[0].link && m.attachments_meta[0].link.url
    ? m.attachments_meta[0].link
    : null,
  file: m.media_name || "",
  duration: m.media_duration || "",
});

// Apply a folder filter to a pulse_conversation query, server-side, so a folder
// reflects the WHOLE mailbox in the DB — not just the recent page the client
// happens to have loaded (which is why old starred/sent mail wasn't showing).
const _notTrashSpam = (q) => q.not("folder", "in", "(trash,spam)");
// Extra toolbar filter applied server-side (so it spans the whole folder, not
// just the loaded page). 'attachments' is handled client-side.
const _applyFilter = (q, filter) => {
  if (filter === "unread") return q.gt("unread_count", 0);
  if (filter === "starred") return q.eq("starred", true);
  // Awaiting = client wrote last, MINUS threads where the AI thread-read says
  // the ball is with the client (acknowledgments — nothing to reply to).
  // Freshness can't be compared column-to-column in PostgREST; ingest re-analyzes
  // on every new message, so waiting_on is at most minutes stale.
  if (filter === "awaiting") return q.eq("awaiting_reply", true).or("ai_meta->>waiting_on.is.null,ai_meta->>waiting_on.eq.us");
  return q;
};
const _applyFolder = (q, folder) => {
  if (!folder || folder === "inbox") return q.eq("folder", "inbox");
  // Starred / Important / Snoozed / labels mirror Gmail: they exclude anything
  // sitting in Trash or Spam (a trashed message keeps its labels but shouldn't
  // show under them).
  if (folder === "starred") return _notTrashSpam(q.eq("starred", true));
  if (folder === "important") return _notTrashSpam(q.eq("is_important", true));
  // Sent = folder 'sent' OR has the SENT label — but exclude trashed/spam so a
  // deleted sent email doesn't keep reappearing (it still carries the SENT label).
  if (folder === "sent") return _notTrashSpam(q.or("folder.eq.sent,label_ids.cs.{SENT}"));
  if (folder === "snoozed") return _notTrashSpam(q.contains("label_ids", ["SNOOZED"]));
  if (folder === "spam") return q.eq("folder", "spam");
  if (folder === "trash") return q.eq("folder", "trash");
  if (folder === "drafts") return q.eq("folder", "drafts");
  // Archived = removed from Inbox but not trashed/spam/sent/draft (Gmail "All Mail"
  // minus the other buckets). folderFromLabels already buckets these as 'archive'.
  if (folder === "archive") return q.eq("folder", "archive");
  if (folder.startsWith("label:")) return _notTrashSpam(q.contains("label_ids", [folder.slice(6)]));
  return q.eq("folder", "inbox");
};

// WHO IS "US" — our own email domains.
// MIRRORS supabase/functions/_shared/domains.ts. The frontend cannot import from
// the edge functions, so this is the one unavoidable second copy — keep them in
// step. It was previously an inline "brandripplr.com" string in eight places, and
// EmailView had a NINTH copy naming the wrong Stars domain entirely
// ("starsmanagement.com" — no such domain), which would have badged every Stars
// colleague's mail as External.
// starsmagnetmanagement.com is Stars Magnet's PRIMARY domain; starsmagnet.com is a
// Workspace ALIAS of it (verified: same mailbox, same message ids). Both must count
// as ours — Gmail does not normalise the alias away in From/Cc headers — but only
// the primary is ever registered as a mailbox.
export const PULSE_INTERNAL_DOMAINS = ["brandripplr.com", "starsmagnetmanagement.com", "starsmagnet.com"];

/** True when this address is one of ours. Suffix match, so subdomains count. */
export const isInternalAddress = (address) => {
  const a = String(address || "").toLowerCase().trim();
  if (!a.includes("@")) return false;
  const domain = a.split("@").pop() || "";
  return PULSE_INTERNAL_DOMAINS.some((d) => domain === d || domain.endsWith(`.${d}`));
};

/**
 * Shared/group inbox addresses — "us", but not a PERSON, so never a thread owner
 * or "the colleague handling this". Mirrors isGroupAddress in
 * supabase/functions/_shared/domains.ts.
 *
 * Was inlined three times in this file with the domain spelled out
 * ("campaigns@brandripplr.com"), which quietly disagreed with the edge-function
 * copy that matches on the local part alone — so a group address on any other
 * domain counted as a person here but not there.
 */
export const isGroupAddress = (address) => {
  const a = String(address || "").toLowerCase();
  // campaigns@ (the Google Group) and campaigns-inbox@, campaigns-inbox2@ … (its
  // member mailboxes). Numbered so a replacement inbox is recognised without a
  // code change — added 23 Sep when campaigns-inbox@ was API-blocked by Google
  // and a second member mailbox was needed to keep Radar fed. Mirrors
  // _shared/domains.ts and _shared/sentiment.ts; change all three together.
  return /(^|[<\s,;"'])campaigns(-inbox\d*)?@/.test(a);
};

/**
 * WHO IS RESPONSIBLE for a thread — the Radar "Responsible" column.
 *
 * On a SHARED inbox (campaigns-inbox@) anyone on the team may pick a thread up, so
 * responsibility is inferred: the last colleague who actually wrote on it.
 *
 * On a PERSONAL mailbox that inference is wrong, and worse, usually empty. Mail
 * sitting in adnan@'s inbox is Adnan's to answer whether or not he has replied yet
 * — and if the client wrote and nobody has answered (exactly the threads Radar is
 * for) there IS no internal sender to infer from, so the column came out blank on
 * the very rows that most needed an owner. So for a personal mailbox the owner IS
 * the mailbox, unconditionally.
 *
 * The NAME comes from `pulse_channel_account.label`, not from anything hardcoded —
 * it's editable in Admin → Email → Edit, so a new mailbox needs no code change.
 * Falls back to the address local part if the label was left as the address.
 */
const _responsibleFor = (conv, handlerMsg) => {
  const mb = conv && conv.mailbox;
  if (mb && mb.kind === "personal" && mb.identifier) {
    const label = String(mb.label || "").trim();
    // A label that's just the address is no more informative than the address.
    const name = (label && label.toLowerCase() !== String(mb.identifier).toLowerCase())
      ? label
      : String(mb.identifier).split("@")[0];
    return { name, email: mb.identifier };
  }
  if (!handlerMsg) return { name: "", email: "" };
  const hp = _parseAddr(handlerMsg.from_address || "");
  return { name: handlerMsg.from_name || hp.name || hp.email, email: hp.email };
};

// Mailboxes Radar watches — `pulse_channel_account.radar` (migration
// 20260824130000). Radar used to be hardcoded to campaigns-inbox@ in six places
// in this file alone; it is now a per-mailbox toggle in Admin → Email.
// Cached per page load: it changes only when an admin flips the toggle, and both
// Radar readers below need it.
let _radarMbs = null;
const _radarMailboxes = async () => {
  if (_radarMbs) return _radarMbs;
  const { data, error } = await supabase.from("pulse_channel_account")
    .select("id, identifier, label, kind").eq("channel", "email").eq("radar", true);
  if (error) throw new Error(error.message);
  _radarMbs = data || [];
  return _radarMbs;
};

// GROUP-RELAYED CLIENT MAIL — see supabase/functions/_shared/sentiment.ts for the
// full reasoning and the measurements. Short version: client mail arriving via the
// campaigns@ Google Group is stored with the GROUP as from_address, so an
// address-only "is this us?" test misclassifies the client as our own team. The
// relay keeps the real author in the display name ("'Karim, May' via Campaigns"),
// which is a header and safe to trust. Keep this in step with the two edge-function
// copies (_shared/sentiment.ts, _shared/gmail.ts).
const _RELAY_SUFFIX = /\s+via\s+Campaigns\b/i;
const _RELAY_BOTS = /creatoriq|acrobat\s*sign|docusign|digisigner|dcsign|otter\.?ai|fireflies|chatgpt|survey\s*monkey|no[-_]?reply|notification/i;
const _isRelayedClient = (m) => {
  const n = String((m && m.from_name) || "");
  if (!_RELAY_SUFFIX.test(n)) return false;
  if (!isInternalAddress(m && m.from_address)) return false;
  return !_RELAY_BOTS.test(n);
};
// Author as the relay recorded them, for display: "'Karim, May' via Campaigns" → "Karim, May".
const _relayAuthor = (m) => String((m && m.from_name) || "")
  .replace(_RELAY_SUFFIX, "").replace(/\s*via\s+.*$/i, "").replace(/^'|'$/g, "").trim();

// Select for the LIST view — omits the heavy message body/html (which can
// be large HTML emails). Those load lazily when a thread is opened, so the list
// query stays small and fast. The row only needs snippet + attachment presence.
const _CONV_SELECT_LIST = `id, channel_account_id, external_thread_id, folder, unread_count, flagged, spam_flag, starred, is_important, label_ids, from_name, from_address, preview, subject,
                 owner_id, campaign_id, campaign_ids, sentiment, status, last_message_at, created_at, first_response_at, awaiting_reply,
                 summary, summary_at, summary_msg_count,
                 mailbox:channel_account_id(identifier),
                 messages:pulse_message(id, direction, from_name, from_address, label_ids, snippet, sent_at, tracking, open_token, attachments_meta, delivery_status, sender:sender_user_id(avatar)),
                 notes:pulse_note(id, body, created_at, author:author_user_id(first_name, last_name, avatar))`;

// Rough device label from a user-agent (for the open-tracking chip).
const _deviceFromUa = (ua) => {
  if (!ua) return "";
  if (/GoogleImageProxy/i.test(ua)) return "Gmail";
  if (/iPhone|Android.*Mobile|Mobile/i.test(ua)) return "Mobile";
  if (/iPad|Tablet/i.test(ua)) return "Tablet";
  if (/Macintosh|Windows|Linux|X11/i.test(ua)) return "Desktop";
  return "";
};

// Outbound message → open-tracking view for the reading pane. Inbound → null.
const _trackingView = (m) => {
  if (m.direction !== "outbound" || !m.open_token) return null;
  const t = (m.tracking && typeof m.tracking === "object") ? m.tracking : {};
  const count = t.open_count || 0;
  return {
    opened: count > 0, count,
    when: t.last_open_at ? _when(t.last_open_at) : null,
    device: _deviceFromUa(t.last_ua),
  };
};

// One pulse_message row → the reading-pane message shape. `body`/`html` are only
// present on the full (per-thread) fetch; the list fetch omits them for speed and
// falls back to the snippet.
const _mapMessage = (m, avatars = {}) => ({
  id: m.id, dir: m.direction === "outbound" ? "out" : "in",
  fromName: _decode(m.from_name || m.from_address || ""), fromEmail: m.from_address || "",
  initials: mock.initials(m.from_name || m.from_address || "?"),
  avatar: m.direction === "outbound" ? _avatarUrl(m.sender?.avatar) : (avatars[(m.from_address || "").toLowerCase()] || null),
  date: _whenFull(m.sent_at), body: _decode(m.body || m.snippet || ""), html: m.html || null, labelIds: m.label_ids || [],
  // Per-message recipients — needed so Reply / Reply all address the message you
  // actually clicked, not whoever happens to sit on the conversation row.
  to: m.to_addresses || [],
  cc: m.cc_addresses || [],
  bcc: m.bcc_addresses || [],
  externalId: m.external_message_id || null,
  attachments: (m.attachments_meta || []).map((a) => ({
    name: a.name, size: a.size, path: a.path || null,
    contentId: a.contentId || null, inline: !!a.inline, mime: a.mime || "",
  })),
  tracking: _trackingView(m),
});

// DB conversation row → the shape EmailView renders (matches the mock shape).
// `full` = whether message bodies/html are loaded (list fetch = false → lazy-load on open).
const _thread = (c, avatars = {}, full = true) => {
  const fromName = c.from_name || c.from_address || "(unknown sender)";
  const fromKey = (c.from_address || "").toLowerCase();
  const msgs = (c.messages || []).slice().sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at));
  const notes = (c.notes || []).slice().sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  return {
    id: c.id, folder: c.folder || "inbox", unread: (c.unread_count || 0) > 0, full,
    flagged: !!c.flagged, phishing: !!c.spam_flag,
    from: { name: _decode(fromName), email: c.from_address || "", initials: mock.initials(fromName), avatar: avatars[fromKey] || null },
    ownerId: c.owner_id, campaignId: c.campaign_id, spans: c.campaign_ids || [],
    mailbox: c.mailbox?.identifier || "", mailboxId: c.channel_account_id, externalThreadId: c.external_thread_id || null,
    subject: _decode(c.subject) || "(no subject)", snippet: _decode(c.preview) || "",
    date: _when(c.last_message_at || c.created_at),
    sla: c.first_response_at ? "Replied" : "Awaiting reply",
    slaTone: c.first_response_at ? "green" : "amber",
    awaiting: !!c.awaiting_reply,
    awaitingSince: c.awaiting_reply ? (c.last_message_at || c.created_at) : null,
    sentiment: c.sentiment || "neutral", open: null,
    // Cached AI thread summary (JSON string) + staleness marker — see
    // pulse-summarize-thread. Null until someone asks for one.
    summary: c.summary || null, summaryAt: c.summary_at || null, summaryMsgCount: c.summary_msg_count || null,
    starred: !!c.starred, important: !!c.is_important, labelIds: c.label_ids || [],
    messages: msgs.map((m) => _mapMessage(m, avatars)),
    notes: notes.map((n) => {
      const who = _fullName(n.author);
      return { ini: mock.initials(who), who, when: _when(n.created_at), text: n.body, avatar: _avatarUrl(n.author?.avatar) };
    }),
  };
};

export const PulseService = {
  // small display helpers reused across views
  initials: mock.initials,
  colorFor: mock.colorFor,
  channelMeta: mock.CHANNEL_META,

  async getTeam() { return ok(mock.TEAM); },
  // Real campaigns (from the `campaign` table) for the picker + thread labels.
  // Active first, then most recent; falls back to the mock list if unreadable.
  async getCampaigns() {
    const { data, error } = await supabase
      .from("campaign")
      .select("id, title, is_active, brand:brand_id(brand_name, company:company_id(company_name))")
      .order("is_active", { ascending: false })
      .order("id", { ascending: false })
      .limit(300);
    if (error || !data) return ok(mock.CAMPAIGNS);
    const palette = ["#db2777", "#b45309", "#0369a1", "#7c3aed", "#059669", "#dc2626", "#0891b2", "#c026d3", "#ea580c", "#4338ca"];
    return ok(data.map((c) => {
      const brand = c.brand?.brand_name || c.brand?.company?.company_name || "";
      return {
        id: c.id,
        label: brand ? `${brand} — ${c.title || "Campaign #" + c.id}` : (c.title || "Campaign #" + c.id),
        color: palette[Number(c.id) % palette.length],
        active: c.is_active === true,
      };
    }));
  },

  // Email conversations awaiting a reply (for Pulse Insights). Longest-waiting
  // first. The "client" shown is the LATEST message's sender (which is external
  // by definition of awaiting), not the thread's original sender.
  // WHO SENT THE CLIENT A TIMELINE, FOR WHICH CAMPAIGN.
  //
  // Reads the columns written at ingest (_shared/timeline.ts) rather than
  // scanning message bodies here: a full-text scan over pulse_message took 5-10s
  // in testing and Radar already does plenty per load.
  //
  // Scoped to the Radar mailboxes like every other board, so switching a mailbox
  // off in Admin removes its rows instead of leaving them behind.
  async getTimelineEmails(limit = 60) {
    let mbIds = [];
    try { mbIds = (await _radarMailboxes()).map((m) => m.id); } catch (e) { return { data: [], error: e.message }; }
    if (!mbIds.length) return ok([]);
    const { data, error } = await supabase.from("pulse_conversation")
      .select("id, subject, campaign_id, timeline_sent_at, timeline_sent_by, timeline_last_sent_at, timeline_count, timeline_stage_count, mailbox:channel_account_id(identifier)")
      .eq("channel", "email")
      .in("channel_account_id", mbIds)
      .not("timeline_sent_at", "is", null)
      .order("timeline_sent_at", { ascending: false })
      .limit(limit);
    if (error) return { data: [], error: error.message };
    const now = Date.now();
    return ok((data || []).map((c) => {
      return {
        id: c.id,
        subject: _decode(c.subject) || "(no subject)",
        campaignId: c.campaign_id,
        sentBy: c.timeline_sent_by || "",
        firstSentAt: c.timeline_sent_at,
        lastSentAt: c.timeline_last_sent_at || c.timeline_sent_at,
        sends: c.timeline_count || 1,
        // HOW OFTEN — average days between sends. Needs at least two sends to
        // mean anything, so a single one reports null and the UI says "once"
        // rather than "0 days", which would read as "constantly".
        // (Quoted copies no longer score, so these dates and counts are real
        // sends — an earlier version measured quoting and called 63 of 72 threads
        // "re-issued". See _shared/timeline.ts.)
        everyDays: (() => {
          const n = c.timeline_count || 1;
          if (n < 2 || !c.timeline_sent_at || !c.timeline_last_sent_at) return null;
          const span = new Date(c.timeline_last_sent_at).getTime() - new Date(c.timeline_sent_at).getTime();
          return Math.max(1, Math.round(span / 86400000 / (n - 1)));
        })(),
        // Days since the client last had a schedule from us.
        staleDays: c.timeline_last_sent_at || c.timeline_sent_at
          ? Math.floor((now - new Date(c.timeline_last_sent_at || c.timeline_sent_at).getTime()) / 86400000)
          : null,
        stages: c.timeline_stage_count || 0,
        mailbox: c.mailbox?.identifier || "",
      };
    }));
  },

  async getAwaitingEmails(limit = 100) {
    // Scoped to the Radar mailboxes explicitly. awaiting_reply is only ever WRITTEN
    // for those, so this is a no-op today — but the flag is not cleared when a
    // mailbox is switched off in Admin, so without this filter turning Radar off
    // would leave that mailbox's threads sitting in the panel forever.
    let mbIds = [];
    try { mbIds = (await _radarMailboxes()).map((m) => m.id); } catch (e) { return { data: [], error: e.message }; }
    if (!mbIds.length) return ok([]);
    const { data: raw, error } = await supabase.from("pulse_conversation")
      .select("id, from_name, from_address, subject, last_message_at, campaign_id, channel_account_id, sentiment, ai_meta, mailbox:channel_account_id(identifier, label, kind)")
      .eq("channel", "email").eq("awaiting_reply", true).eq("awaiting_muted", false)
      .in("channel_account_id", mbIds)
      .order("last_message_at", { ascending: true }).limit(limit);
    if (error) return { data: [], error: error.message };
    // "Client wrote last" isn't always "we owe a reply" — acknowledgments like
    // "we'll review and get back to you" need nothing from us. Trust a FRESH
    // AI read (analyzed after the latest message) that puts the ball elsewhere.
    //
    // REVERTED 2026-09-08: an earlier attempt also pulled in threads where WE
    // replied but the AI judged the reply a holding one ("acknowledged only").
    // It surfaced real gaps, but the team's objection stands and is the stronger
    // argument: once someone has responded, a panel called "Awaiting response"
    // must not still list the thread — otherwise the list stops meaning anything
    // and people stop trusting it. Those threads live on "Replied but unresolved",
    // which is where that distinction belongs. Do not re-add without moving it to
    // its own board or renaming this one.
    const convs = (raw || []).filter((c) => !_aiSaysNoReplyNeeded(c));
    const ids = (convs || []).map((c) => c.id);
    const latest = {};   // newest message overall (the client we owe a reply)
    const handler = {};  // newest colleague who actually WROTE TO THE CLIENT on this thread
    if (ids.length) {
      // Paged + oldest-first, so walk it in reverse to get "newest wins".
      const msgs = await _messagesForConversations(ids, "conversation_id, from_name, from_address, to_addresses, cc_addresses, sent_at");
      for (let i = msgs.length - 1; i >= 0; i--) {
        const m = msgs[i];
        const cid = m.conversation_id;
        if (!latest[cid]) latest[cid] = m;
        const f = (m.from_address || "").toLowerCase();
        // Three conditions, and the third one is the subtle one:
        //  1. internal — a colleague, not the client
        //  2. not a group address — "us" but not a PERSON, so never a name to show
        //  3. ADDRESSED TO THE CLIENT — internal chatter lands in this shared inbox
        //     too ("Rayane, can you look at this?"), and crediting that as handling
        //     the thread names someone who never wrote to the client. Observed on
        //     "Re: Dr Salma EPP Content W1 Issue" (Beiersdorf), where Rayane was
        //     shown Responsible for a note she sent to a teammate.
        // The same guard already existed in getInsightsAnalytics (`toClient`) and in
        // _shared/sentiment.ts, where it is documented against conv 54690 — this
        // column was the one place missing it.
        const wroteToClient = [...(m.to_addresses || []), ...(m.cc_addresses || [])]
          .map((x) => String(x || "").toLowerCase())
          .some((a) => a.includes("@") && !isInternalAddress(a));
        if (!handler[cid] && isInternalAddress(f) && !isGroupAddress(f) && wroteToClient) handler[cid] = m;
      }
    }
    return ok((convs || []).map((c) => {
      const lm = latest[c.id];
      const p = _parseAddr(lm ? (lm.from_address || "") : (c.from_address || ""));
      // Relayed client mail: show the real author, not "'X' via Campaigns".
      const nm = (lm && _isRelayedClient(lm))
        ? _relayAuthor(lm)
        : ((lm ? lm.from_name : c.from_name) || p.name || p.email);
      const resp = _responsibleFor(c, handler[c.id]);
      return {
        id: c.id, fromName: nm, fromEmail: p.email,
        subject: _decode(c.subject) || "(no subject)",
        since: c.last_message_at, campaignId: c.campaign_id,
        mailbox: c.mailbox?.identifier || "",
        responsibleName: resp.name,
        responsibleEmail: resp.email,
        sentiment: c.sentiment || "neutral",
        summary: (c.ai_meta && c.ai_meta.summary) || "",
        risks: (c.ai_meta && c.ai_meta.risks) || [],
      };
    }));
  },

  // ---- Radar: timeline coverage (campaign-driven) -----------------------
  // Every active campaign with a CM assigned, and whether its timeline email is
  // up to date. Rule = the Tracker's client-update cadence (live: daily,
  // active: 2 wd, seeding: 3 wd); evidence = timeline emails Pulse detected.
  // See migration 20260930100000 for the exact campaign set.
  async getTimelineCoverage() {
    const { data, error } = await supabase.rpc("pulse_timeline_coverage");
    if (error) return { data: [], error: error.message };
    const now = Date.now();
    return ok((data || []).map((r) => {
      const due = r.due_at ? new Date(r.due_at).getTime() : null;
      const last = r.last_timeline_at ? new Date(r.last_timeline_at).getTime() : null;
      const overdueMs = due ? now - due : 0;
      // Status buckets the board colours on:
      //   never   — no timeline email ever detected for this campaign
      //   overdue — past due_at
      //   due     — due today (within the next 24h)
      //   ok      — next one not due yet
      const status = !last ? "never" : overdueMs > 0 ? "overdue" : (due - now < 864e5 ? "due" : "ok");
      return {
        campaignId: r.campaign_id, title: r.campaign_title || `#${r.campaign_id}`, brand: r.brand_name || "",
        ownerId: r.owner_user_id || null, owner: r.owner_user_name || "", highPriority: !!r.high_priority,
        stage: r.campaign_stage, cadence: r.cadence_working_days, allocatedAt: r.allocation_date,
        lastAt: r.last_timeline_at, lastBy: r.last_timeline_by || "", lastConvId: r.last_timeline_conversation_id || null,
        sends: Number(r.timeline_count) || 0, dueAt: r.due_at, status,
        overdueDays: overdueMs > 0 ? Math.floor(overdueMs / 864e5) : 0,
      };
    }));
  },

  // ---- Radar: WhatsApp groups --------------------------------------------
  // Same ai_meta packet as email (see _shared/chat_intel.ts), read straight off
  // the conversation rows. One list, classified into what the board needs:
  //   waiting   — client asked, we have not answered (waiting_on us)
  //   negative  — client unhappy in the latest window
  //   promise   — we promised something not yet delivered (overdue if past date)
  //   client    — we asked, client has not come back
  // Groups muted in Chats (internal ones) are excluded. Scope = listener numbers
  // with Radar on (Admin → WhatsApp).
  async getChatRadar() {
    const { data: accts, error: aErr } = await supabase.from("pulse_channel_account")
      .select("id, label").eq("channel", "whatsapp").eq("radar", true);
    if (aErr) return { data: [], error: aErr.message };
    const ids = (accts || []).map((a) => a.id);
    if (!ids.length) return ok([]);
    const { data, error } = await supabase.from("pulse_conversation")
      .select("id, from_name, campaign_id, last_message_at, sentiment, ai_meta, awaiting_reply, awaiting_muted, ask_resolved_at, promise_done_at, waiting_cleared_at, wa_group_status, wa_avatar_path")
      .eq("channel", "whatsapp").eq("is_group", true).in("channel_account_id", ids)
      .eq("awaiting_muted", false).not("ai_meta", "is", null)
      .order("last_message_at", { ascending: false }).limit(300);
    if (error) return { data: [], error: error.message };
    // Owner = last of our team who posted (sender_user_id), one query for all rows.
    const cids = (data || []).map((c) => c.id);
    const owner = {};
    if (cids.length) {
      const { data: ours } = await supabase.from("pulse_message")
        .select("conversation_id, from_name, sent_at").in("conversation_id", cids)
        .eq("direction", "outbound").not("sender_user_id", "is", null)
        .order("sent_at", { ascending: false }).limit(2000);
      (ours || []).forEach((m) => { if (!owner[m.conversation_id]) owner[m.conversation_id] = m.from_name || ""; });
    }
    const today = new Date().toISOString().slice(0, 10);
    const rows = (data || []).map((c) => {
      const m = c.ai_meta || {};
      // Every open thread of work in the group (ai_meta.items, see chat_intel.ts),
      // minus the ones someone closed by hand (ai_meta.done[id]). Older analyses
      // only carry the single ask / promise — synthesise one item each from those
      // so nothing disappears until the group is re-read.
      const doneMap = (m.done && typeof m.done === "object") ? m.done : {};
      let items = (Array.isArray(m.items) ? m.items : []).filter((it) => it && it.id && it.status !== "done" && !doneMap[it.id]);
      if (!Array.isArray(m.items)) {
        if (m.ask && m.ask_resolved !== true && !c.ask_resolved_at && m.waiting_on === "us") items.push({ id: "ask", kind: "ask", text: m.ask, who: null, raised_at: (m.last_client_at || "").slice(0, 10) || null, deadline_text: m.deadline_text || null, deadline_date: m.deadline_date || null, status: m.reply_type === "acknowledgment" ? "acknowledged" : "open" });
        if (m.our_promise && m.promise_kept !== true && !c.promise_done_at) items.push({ id: "promise", kind: "promise", text: m.our_promise, who: owner[c.id] || null, raised_at: (m.last_reply_at || "").slice(0, 10) || null, deadline_text: m.promise_deadline_text || null, deadline_date: m.promise_deadline_date || null, status: "open" });
      }
      items = items.map((it) => ({
        ...it,
        overdue: !!(it.deadline_date && it.deadline_date < today),
        ageDays: it.raised_at ? Math.max(0, Math.round((Date.now() - new Date(it.raised_at).getTime()) / 864e5)) : null,
      }));
      // Worst first inside the group: overdue, then asks before promises, then oldest.
      items.sort((a, b) => (b.overdue - a.overdue) || ((b.kind === "ask") - (a.kind === "ask")) || ((a.raised_at || "") < (b.raised_at || "") ? -1 : 1));

      const asks = items.filter((it) => it.kind === "ask");
      const promises = items.filter((it) => it.kind === "promise");
      const waiting = asks.length > 0;
      const promiseOpen = promises.length > 0;
      const overdue = promises.some((it) => it.overdue);
      const clientOwes = m.waiting_on === "client" && !c.waiting_cleared_at;
      const negative = c.sentiment === "negative";
      const sinceClient = m.last_client_at ? (Date.now() - new Date(m.last_client_at).getTime()) / 36e5 : 0;
      const top = asks[0] || promises[0] || null;
      return {
        id: c.id, name: c.from_name || "WhatsApp group", campaignId: c.campaign_id,
        sentiment: c.sentiment || "neutral", summary: m.summary || "", ask: (asks[0] && asks[0].text) || m.ask || "",
        deadlineText: (asks[0] && asks[0].deadline_text) || "", deadlineDate: (asks[0] && asks[0].deadline_date) || null,
        promise: (promises[0] && promises[0].text) || "", promiseDue: (promises[0] && promises[0].deadline_date) || null, promiseDueText: (promises[0] && promises[0].deadline_text) || "",
        items, openAsks: asks.length, openPromises: promises.length, top,
        risks: m.risks || [], blockers: m.blockers || [], topic: m.topic || "other", slips: m.promise_slips || 0,
        waitingOn: m.waiting_on || "none", replyType: m.reply_type || "none",
        waiting, negative, promiseOpen, overdue, clientOwes,
        waitingHours: waiting ? Math.round(sinceClient) : 0,
        lastClientAt: m.last_client_at || null, analyzedAt: m.analyzed_at || null, lastMessageAt: c.last_message_at,
        owner: owner[c.id] || "",
        // dirty = new messages since the last read (the sweep will re-analyse shortly)
        stale: !!(m.analyzed_at && c.last_message_at && new Date(c.last_message_at) > new Date(m.analyzed_at)),
      };
    }).filter((r) => r.waiting || r.negative || r.promiseOpen || r.clientOwes);
    // Worst first: negative, then overdue promises, then most open items, then waiting longest.
    rows.sort((a, b) => (b.negative - a.negative) || (b.overdue - a.overdue) || (b.items.length - a.items.length) || (b.waitingHours - a.waitingHours));
    return ok(rows);
  },

  // Close one open item in a WhatsApp group by hand (answered on a call, dropped,
  // or the AI misread it). Stored in ai_meta.done[id] = {at, by}; the analyser
  // keeps that map across re-reads so the item cannot come back.
  async markChatItemDone(conversationId, itemId, done = true) {
    const { data: uid } = await this.getMyUserId();
    const { data: c, error: rErr } = await supabase.from("pulse_conversation").select("ai_meta").eq("id", conversationId).maybeSingle();
    if (rErr || !c) return { data: false, error: rErr ? rErr.message : "not found" };
    const meta = { ...(c.ai_meta || {}) };
    const map = { ...((meta.done && typeof meta.done === "object") ? meta.done : {}) };
    if (done) map[itemId] = { at: new Date().toISOString(), by: uid || null }; else delete map[itemId];
    meta.done = map;
    const items = Array.isArray(meta.items) ? meta.items.filter((it) => it && !map[it.id]) : [];
    const stillAsk = items.some((it) => it.kind === "ask");
    const { error } = await supabase.from("pulse_conversation")
      .update({ ai_meta: meta, awaiting_reply: stillAsk }).eq("id", conversationId);
    return error ? { data: false, error: error.message } : ok(true);
  },

  // ---- Radar: WhatsApp team scorecard --------------------------------------
  // Mirror of the email scorecard for listener groups. A "reply" is a message
  // from one of us (sender_user_id set at ingest) that follows client messages;
  // its response time is measured from the FIRST client message of that burst —
  // i.e. from when the client started waiting, not from their last line.
  // Awaiting = groups where Radar says the client is waiting on us, credited to
  // the last of us who posted there.
  async getChatScorecard(days = 45) {
    const { data: accts } = await supabase.from("pulse_channel_account").select("id").eq("channel", "whatsapp").eq("radar", true);
    const ids = (accts || []).map((a) => a.id);
    if (!ids.length) return ok([]);
    const { data: convs, error } = await supabase.from("pulse_conversation")
      .select("id, ai_meta, ask_resolved_at, awaiting_muted")
      .eq("channel", "whatsapp").eq("is_group", true).in("channel_account_id", ids).eq("awaiting_muted", false);
    if (error) return { data: [], error: error.message };
    const cids = (convs || []).map((c) => c.id);
    if (!cids.length) return ok([]);
    const since = new Date(Date.now() - days * 864e5).toISOString();
    const msgs = [];
    for (let from = 0; from < 20000; from += _PAGE) {
      const { data } = await supabase.from("pulse_message")
        .select("conversation_id, direction, type, sender_user_id, sent_at, sender:sender_user_id(email, first_name, last_name)")
        .in("conversation_id", cids).neq("type", "system").gte("sent_at", since)
        .order("sent_at", { ascending: true }).range(from, from + _PAGE - 1);
      if (!data || !data.length) break;
      msgs.push(...data);
      if (data.length < _PAGE) break;
    }
    const median = (arr) => { if (!arr.length) return 0; const s = [...arr].sort((a, b) => a - b); const m = Math.floor(s.length / 2); return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; };
    const person = {};
    const P = (m) => {
      const key = m.sender_user_id;
      if (!person[key]) person[key] = { userId: key, name: _fullName(m.sender) || (m.sender && m.sender.email) || "?", email: ((m.sender && m.sender.email) || "").toLowerCase(), replies: 0, groups: new Set(), resp: [], awaiting: 0 };
      return person[key];
    };
    const pending = {};   // convId -> when the client started waiting (first client msg since our last reply)
    const lastOurs = {};  // convId -> user id of the last of us who posted
    msgs.forEach((m) => {
      const cid = m.conversation_id;
      if (m.direction === "outbound") {
        if (!m.sender_user_id) return;
        const p = P(m); p.replies++; p.groups.add(cid); lastOurs[cid] = m.sender_user_id;
        if (pending[cid]) { p.resp.push(new Date(m.sent_at) - new Date(pending[cid])); pending[cid] = null; }
      } else if (!pending[cid]) pending[cid] = m.sent_at;
    });
    (convs || []).forEach((c) => {
      const m = c.ai_meta || {};
      if (m.ask && m.ask_resolved !== true && !c.ask_resolved_at && m.waiting_on === "us" && lastOurs[c.id] && person[lastOurs[c.id]]) person[lastOurs[c.id]].awaiting++;
    });
    return ok(Object.values(person).map((p) => ({
      userId: p.userId, name: p.name, email: p.email, replies: p.replies, groups: p.groups.size,
      medianRespMins: p.resp.length ? Math.round(median(p.resp) / 60000) : null, awaiting: p.awaiting,
    })).sort((a, b) => b.replies - a.replies));
  },

  // Insights analytics — computed over the mailboxes Radar watches (the `radar`
  // flag, Admin → Email; originally just campaigns-inbox@). Inbound/outbound is
  // judged by SENDER DOMAIN, not message.direction: a group-copy inbox receives
  // our own team's replies as inbound mail, so direction is meaningless there.
  // External = client, one of our domains = team.
  async getInsightsAnalytics() {
    const empty = {
      scope: "",
      kpis: { total: 0, responded: 0, responseRate: 0, medianFirstMins: 0, avgFirstMins: 0, awaiting: 0, negative: 0 },
      aging: { lt4h: 0, h4to24: 0, d1to3: 0, gt3d: 0 }, slaBreach: 0,
      volumeByDay: [], heatmap: [], channels: { email: 0, whatsapp: 0, calls: 0 },
      perPerson: [], unassignedAwaiting: 0,
      sentiment: { positive: 0, neutral: 0, negative: 0 }, sentimentTrend: [], sentimentByCampaign: [],
      topCampaigns: [], goingQuiet: [], highEffort: [],
      topics: {}, riskCounts: {}, commitments: [], promises: [], unresolved: [], promisesOverdue: 0,
      reliability: { kept: 0, broken: 0, rate: null }, blockerCounts: {}, slipping: [], missingAttachment: [], waitingOnClient: [], needsChase: 0,
    };
    let mbs = [];
    try { mbs = await _radarMailboxes(); } catch (e) { return { data: empty, error: e.message }; }
    if (!mbs.length) return ok(empty);
    const mbIds = mbs.map((m) => m.id);
    const personalMbIds = new Set(mbs.filter((m) => m.kind === "personal").map((m) => m.id));
    empty.scope = mbs.map((m) => m.identifier).join(", ");
    // Page this too — `.limit(3000)` is a lie once PostgREST's 1000-row cap
    // kicks in, and it fails silently (377 threads today, but it grows — and it
    // now spans every Radar mailbox, not just one).
    const CONV_SEL = "id, channel_account_id, from_address, subject, first_response_at, last_message_at, created_at, awaiting_reply, awaiting_muted, sentiment, campaign_id, ai_meta, ask_resolved_at, ask_resolved_by, promise_done_at, promise_done_by, waiting_cleared_at, waiting_cleared_by";
    const convs = [];
    for (let from = 0; from < 20000; from += _PAGE) {
      const { data, error } = await supabase.from("pulse_conversation").select(CONV_SEL)
        .eq("channel", "email").in("channel_account_id", mbIds)
        .order("id", { ascending: true }).range(from, from + _PAGE - 1);
      if (error || !data || !data.length) break;
      convs.push(...data);
      if (data.length < _PAGE) break;
    }
    // A dismissed (muted) thread is not a client awaiting a reply, and neither
    // is one where a fresh AI read says the ball is with the client (e.g. an
    // acknowledgment: "we'll review and get back to you").
    const rows = (convs || []).map((c) => ((c.awaiting_muted || _aiSaysNoReplyNeeded(c)) ? { ...c, awaiting_reply: false } : c));
    const ids = rows.map((c) => c.id);
    const msgs = ids.length
      ? await _messagesForConversations(ids, "conversation_id, from_name, from_address, to_addresses, cc_addresses, sent_at")
      : [];
    // channel volume (global): email = this inbox, plus whatsapp + calls counts
    let whatsapp = 0; let calls = 0;
    try { const r = await supabase.from("pulse_conversation").select("id", { count: "exact", head: true }).eq("channel", "whatsapp"); whatsapp = r.count || 0; } catch { /* noop */ }
    try { const r = await supabase.from("pulse_call").select("id", { count: "exact", head: true }); calls = r.count || 0; } catch { /* noop */ }

    // A relayed client message arrives from our own group address, so an
    // address-only test calls it "internal" and Radar concludes WE spoke last —
    // leaving answered threads sitting in Waiting on client. Judge by message.
    // Takes the MESSAGE, not the address — the address alone cannot tell a
    // teammate from a client the group relayed on their behalf.
    const internal = (m) => isInternalAddress(m && m.from_address) && !_isRelayedClient(m);
    // Was this message actually SENT TO THE CLIENT? Internal team chatter lands in
    // this shared inbox too, and "yes I'm on it" to a colleague must not read as
    // having answered the client. Recipient arrays can split mid display-name, so
    // only judge entries containing "@".
    const toClient = (m) => [...(m.to_addresses || []), ...(m.cc_addresses || [])]
      .map((x) => String(x || "").toLowerCase())
      .some((a) => a.includes("@") && !isInternalAddress(a));
    const median = (arr) => { if (!arr.length) return 0; const s = [...arr].sort((a, b) => a - b); const m = Math.floor(s.length / 2); return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; };
    const now = Date.now();

    const byConv = {}; msgs.forEach((m) => { (byConv[m.conversation_id] = byConv[m.conversation_id] || []).push(m); });
    const convById = {}; rows.forEach((c) => { convById[c.id] = c; });

    // ---- related threads (LINK, don't merge) ----
    // Clients and teammates often continue the same discussion in a NEW email
    // thread (a fresh subject, or a reply that breaks threading). Each becomes its
    // own conversation, so Radar can show two rows for one piece of work. We do
    // NOT merge them — merging would hide genuinely separate asks — we just
    // surface "there are N sibling threads" so nobody double-chases.
    // Two link rules: identical normalised subject, or same campaign + same client
    // domain. Both are advisory only.
    const normSubj = (x) => String(x || "")
      .replace(/^(\s*(re|fw|fwd|rif|aw)\s*:\s*|\s*\[external\]\s*)+/gi, "")
      .replace(/\s+/g, " ").trim().toLowerCase();
    const keysOf = (c) => {
      const ms = byConv[c.id] || [];
      const ext = [...ms].reverse().find((x) => !internal(x));
      const dom = (_parseAddr((ext || {}).from_address || "").email.split("@")[1] || "");
      const ks = [];
      const s = normSubj(c.subject);
      if (s) ks.push("s:" + s);
      if (c.campaign_id && dom) ks.push("c:" + c.campaign_id + "|" + dom);
      return ks;
    };
    const byKey = {};
    rows.forEach((c) => keysOf(c).forEach((k) => { (byKey[k] = byKey[k] || []).push(c.id); }));
    const relatedOf = (c) => {
      const seen = new Set();
      keysOf(c).forEach((k) => (byKey[k] || []).forEach((id) => { if (id !== c.id) seen.add(id); }));
      return [...seen].slice(0, 6).map((id) => ({
        convId: id, subject: _decode((convById[id] || {}).subject) || "(no subject)",
      }));
    };

    // ---- response time, response rate, per-person, effort ----
    const firstRespMs = [];
    const person = {}; // email -> { name, replies, threads:Set, resp:[], awaiting }
    const P = (m) => { const e = _parseAddr(m.from_address).email; if (!person[e]) person[e] = { name: m.from_name || _parseAddr(m.from_address).name || e, email: e, replies: 0, threads: new Set(), resp: [], awaiting: 0 }; return person[e]; };
    let responded = 0; let withExt = 0;
    const effort = [];
    rows.forEach((c) => {
      const ms = byConv[c.id] || [];
      const firstExt = ms.find((m) => !internal(m));
      if (firstExt) withExt++;
      const reply = firstExt ? ms.find((m) => internal(m) && !isGroupAddress(m.from_address) && new Date(m.sent_at) >= new Date(firstExt.sent_at)) : null;
      if (reply) { responded++; if (firstExt) { const dt = new Date(reply.sent_at) - new Date(firstExt.sent_at); firstRespMs.push(dt); P(reply).resp.push(dt); } }
      // per-person replies + threads
      ms.forEach((m) => { if (internal(m) && !isGroupAddress(m.from_address)) { const p = P(m); p.replies++; p.threads.add(c.id); } });
      // responsible = last internal non-group sender; awaiting attribution
      if (c.awaiting_reply) {
        const lastInt = [...ms].reverse().find((m) => internal(m) && !isGroupAddress(m.from_address));
        if (lastInt) P(lastInt).awaiting++;
      }
      effort.push({ convId: c.id, count: ms.length, client: _parseAddr((firstExt || {}).from_address || c.from_address).email, subject: _decode(c.subject) || "(no subject)" });
    });

    // ---- awaiting aging + SLA ----
    const aging = { lt4h: 0, h4to24: 0, d1to3: 0, gt3d: 0 }; let slaBreach = 0; let unassignedAwaiting = 0;
    rows.filter((c) => c.awaiting_reply).forEach((c) => {
      const hrs = (now - new Date(c.last_message_at || c.created_at).getTime()) / 3600000;
      if (hrs < 4) aging.lt4h++; else if (hrs < 24) aging.h4to24++; else if (hrs < 72) aging.d1to3++; else aging.gt3d++;
      if (hrs >= 24) slaBreach++;
      const ms = byConv[c.id] || [];
      // "Unassigned" means nobody on our side has picked this up. On a PERSONAL
      // mailbox somebody always has: the mailbox owner (see _responsibleFor). Only
      // the shared inbox can genuinely be unowned, so don't count personal threads
      // as unassigned just because the owner hasn't replied yet.
      const ownedByMailbox = personalMbIds.has(c.channel_account_id);
      if (!ownedByMailbox && !ms.some((m) => internal(m) && !isGroupAddress(m.from_address))) unassignedAwaiting++;
    });

    // ---- volume by day (last 30d) + heatmap (client msgs) ----
    const dayMap = {}; const heat = Array.from({ length: 7 }, () => new Array(24).fill(0));
    const start = now - 30 * 86400000;
    msgs.forEach((m) => {
      const t = new Date(m.sent_at); const ts = t.getTime(); if (isNaN(ts)) return;
      const ext = !internal(m);
      if (ts >= start) { const d = m.sent_at.slice(0, 10); (dayMap[d] = dayMap[d] || { day: d, inbound: 0, outbound: 0 }); if (ext) dayMap[d].inbound++; else dayMap[d].outbound++; }
      if (ext) heat[t.getDay()][t.getHours()]++;
    });
    const volumeByDay = [];
    for (let i = 29; i >= 0; i--) { const d = new Date(now - i * 86400000).toISOString().slice(0, 10); volumeByDay.push(dayMap[d] || { day: d, inbound: 0, outbound: 0 }); }

    // ---- sentiment ----
    const sentiment = { positive: 0, neutral: 0, negative: 0 };
    const trendMap = {}; const sentByCamp = {};
    rows.forEach((c) => {
      // Only count threads the AI actually analysed — every row carries the DB
      // default 'neutral', which would drown the real distribution.
      if (!c.ai_meta) return;
      const raw = c.sentiment || "neutral";
      const key = (raw === "positive" || raw === "negative") ? raw : "neutral";
      sentiment[key]++;
      const wk = _weekKey(c.last_message_at || c.created_at);
      const t = (trendMap[wk] = trendMap[wk] || { week: wk, positive: 0, neutral: 0, negative: 0 });
      t[key]++;
      if (c.campaign_id) {
        const b = (sentByCamp[c.campaign_id] = sentByCamp[c.campaign_id] || { campaignId: c.campaign_id, positive: 0, neutral: 0, negative: 0, total: 0 });
        b[key]++; b.total++;
      }
    });
    const sentimentTrend = Object.values(trendMap).sort((a, b) => (a.week < b.week ? -1 : 1)).slice(-8);

    // ---- campaigns: activity, going-quiet, sentiment ----
    const campMsgs = {}; const campLast = {};
    msgs.forEach((m) => { const c = convById[m.conversation_id]; if (c && c.campaign_id) { campMsgs[c.campaign_id] = (campMsgs[c.campaign_id] || 0) + 1; const t = new Date(m.sent_at).getTime(); if (!campLast[c.campaign_id] || t > campLast[c.campaign_id]) campLast[c.campaign_id] = t; } });
    const campIds = [...new Set([...Object.keys(campMsgs), ...Object.keys(sentByCamp).map(String)])].map(Number);
    const labelMap = {};
    if (campIds.length) { const { data: cs } = await this.getCampaignsByIds(campIds); (cs || []).forEach((c) => { labelMap[c.id] = c.label; }); }
    const lbl = (id) => labelMap[id] || ("Campaign #" + id);
    const topCampaigns = Object.entries(campMsgs).map(([id, n]) => ({ campaignId: Number(id), label: lbl(Number(id)), messages: n })).sort((a, b) => b.messages - a.messages).slice(0, 6);
    const goingQuiet = Object.entries(campLast).map(([id, t]) => ({ campaignId: Number(id), label: lbl(Number(id)), daysQuiet: Math.floor((now - t) / 86400000) })).filter((x) => x.daysQuiet >= 14).sort((a, b) => b.daysQuiet - a.daysQuiet).slice(0, 8);
    const sentimentByCampaign = Object.values(sentByCamp).map((x) => ({ ...x, label: lbl(x.campaignId) })).filter((x) => x.negative > 0 || x.positive > 0).sort((a, b) => b.negative - a.negative || b.total - a.total).slice(0, 8);

    // ---- AI email intel: topics, risks, client asks, OUR promises, unresolved ----
    const topics = {}; const riskCounts = {}; const commitments = [];
    const promises = []; const unresolved = [];
    const blockerCounts = {}; const slipping = []; const missingAttachment = []; const waitingOnClient = [];
    let kept = 0; let broken = 0;
    const today = new Date().toISOString().slice(0, 10);
    rows.forEach((c) => {
      const m = c.ai_meta || null;
      if (!m) return;
      // NOTE: deliberately NOT skipping `awaiting_muted` threads here. Dismiss
      // means "no client is waiting on us" (it clears the Awaiting list) — it does
      // NOT mean "stop tracking work on this thread". Hiding muted threads from
      // Open asks / Our promises removed 15 rows including real client asks
      // (Dettol "share the updated video", 7× D&M Halloween). To take a genuine
      // non-client out of Radar entirely, mute the SENDER, which stops it at
      // ingestion instead.
      if (m.topic) topics[m.topic] = (topics[m.topic] || 0) + 1;
      (m.risks || []).forEach((r) => { riskCounts[r] = (riskCounts[r] || 0) + 1; });
      const ms = byConv[c.id] || [];
      const lastExtMsg = [...ms].reverse().find((x) => !internal(x));
      const clientEmail = _parseAddr((lastExtMsg || {}).from_address || c.from_address || "").email;
      // Owner = the last team member (non-group) who wrote on the thread — the
      // person holding it. Shared across promises / asks / unresolved / chases.
      const lastInt = [...ms].reverse().find((x) => internal(x) && !isGroupAddress(x.from_address));
      const owner = lastInt ? (lastInt.from_name || _parseAddr(lastInt.from_address).name || _parseAddr(lastInt.from_address).email) : "";
      // Last message we actually sent the CLIENT (internal notes don't count).
      const lastToClient = [...ms].reverse().find((x) => internal(x) && !isGroupAddress(x.from_address) && toClient(x));
      // Sibling threads covering the same work — advisory, so nobody double-chases.
      const related = relatedOf(c);
      // Manual overrides. The AI only sees email — plenty gets closed on a call
      // or in WhatsApp — so a person can mark an ask answered / promise done.
      // The override LAPSES if the client has written since, otherwise today's
      // "handled" would bury tomorrow's ask on the same thread.
      const clientSpokeAt = lastExtMsg ? new Date(lastExtMsg.sent_at).getTime() : 0;
      const stillValid = (ts) => !!ts && new Date(ts).getTime() >= clientSpokeAt;
      const askDoneByHand = stillValid(c.ask_resolved_at);
      const promiseDoneByHand = stillValid(c.promise_done_at);
      // Promises WE made — the accountability counterpart to client asks.
      // Two exclusions, both "don't accuse the team of missing something they did":
      //  1. promise_kept === true → delivered, nothing to chase.
      //  2. a teammate mailed THE CLIENT after the AI last looked → the delivery
      //     may be in that message. Can't call it overdue on stale evidence; flag
      //     as pending re-check (ingest re-analyzes, so it self-heals). An internal
      //     note is NOT delivery, so it must not clear the promise either.
      if (m.our_promise && m.promise_kept !== true && !promiseDoneByHand) {
        const sentAfterAnalysis = !!(m.analyzed_at && lastToClient && lastToClient.sent_at
          && new Date(lastToClient.sent_at) > new Date(m.analyzed_at));
        promises.push({
          convId: c.id, promise: m.our_promise,
          // When we said it: the reply the promise was read from (ai_meta), else
          // our latest client-facing message.
          promisedAt: m.last_reply_at || (lastToClient ? lastToClient.sent_at : null),
          deadlineText: m.promise_deadline_text || "", deadlineDate: m.promise_deadline_date || null,
          overdue: !sentAfterAnalysis && !!(m.promise_deadline_date && m.promise_deadline_date < today),
          unverified: sentAfterAnalysis,
          owner,
          client: clientEmail, subject: _decode(c.subject) || "(no subject)", when: c.last_message_at, related,
        });
      }
      // Blockers holding delivery up (creator / client approval / finance / …).
      (m.blockers || []).forEach((b) => { blockerCounts[b] = (blockerCounts[b] || 0) + 1; });
      // Promise reliability: only counts promises that came due.
      if (m.promise_kept === true) kept++;
      if (m.promise_kept === false) broken++;
      // Date keeps moving on the same deliverable.
      if ((m.promise_slips || 0) >= 2) {
        slipping.push({
          convId: c.id, promise: m.our_promise || m.ask || "", slips: m.promise_slips,
          client: clientEmail, subject: _decode(c.subject) || "(no subject)",
          deadlineText: m.promise_deadline_text || "",
        });
      }
      // We said "attached" and sent nothing.
      if (m.attachment_missing) {
        missingAttachment.push({
          convId: c.id, client: clientEmail, subject: _decode(c.subject) || "(no subject)", when: m.last_reply_at || c.last_message_at,
        });
      }
      // We asked THEM something and they've gone quiet — work blocked on client.
      // Cleared by hand? Scoped to THIS board only — never `return` here, or the
      // thread would also vanish from Open asks / promises / blockers, which is the
      // over-reach we already made once with dismiss.
      const lastAny = ms.length ? ms[ms.length - 1] : null;
      const waitingClearedByHand = !!(c.waiting_cleared_at && lastAny && lastAny.sent_at
        && new Date(c.waiting_cleared_at).getTime() >= new Date(lastAny.sent_at).getTime());
      if (m.waiting_on === "client" && !waitingClearedByHand) {
        // Count from the thread's LAST message, whoever sent it — not from our own
        // reply. Clients often send a holding reply ("management is still
        // reviewing"), and dating from our email made those read "Quiet for 6d"
        // when they had in fact answered 6 days ago. Thread-idle time is also the
        // honest basis for chasing: their ack resets the clock.
        const since = (lastAny && lastAny.sent_at) || m.last_reply_at || c.last_message_at;
        const days = since ? Math.floor((Date.now() - new Date(since).getTime()) / 86400000) : 0;
        // Did they actually come back to us (even if only to say "still looking")?
        const acked = !!(lastAny && !internal(lastAny));
        waitingOnClient.push({
          convId: c.id, client: clientEmail, subject: _decode(c.subject) || "(no subject)",
          summary: m.summary || "", days, since, acked, needsChase: days >= 3, owner, related,
        });
      }
      // Replied, but our reply didn't actually address the ask (holding replies).
      if (m.ask && m.ask_resolved === false && m.reply_type !== "none" && !askDoneByHand) {
        unresolved.push({
          convId: c.id, ask: m.ask, replyType: m.reply_type,
          client: clientEmail, subject: _decode(c.subject) || "(no subject)", when: c.last_message_at, owner, related,
        });
      }
      // Open asks = what the CLIENT is still waiting on. Mirrors the promises
      // rules: drop asks we've answered (ask_resolved === true), and never call an
      // ask overdue on stale evidence — if we mailed the client after the AI last
      // looked, the answer may be in that message (internal notes don't count).
      if (m.ask && m.ask_resolved !== true && !askDoneByHand) {
        const lastExt = [...ms].reverse().find((x) => !internal(x));
        const askUnverified = !!(m.analyzed_at && lastToClient && lastToClient.sent_at
          && new Date(lastToClient.sent_at) > new Date(m.analyzed_at));
        commitments.push({
          convId: c.id, ask: m.ask, summary: m.summary || "",
          // When the client asked — the client message the ask was read from.
          askedAt: lastExtMsg ? lastExtMsg.sent_at : null,
          deadlineText: m.deadline_text || "", deadlineDate: m.deadline_date || null,
          awaiting: !!c.awaiting_reply, subject: _decode(c.subject) || "(no subject)",
          client: _parseAddr((lastExt || {}).from_address || c.from_address || "").email,
          owner,
          overdue: !askUnverified && !!(m.deadline_date && new Date(m.deadline_date + "T23:59:59") < new Date()),
          unverified: askUnverified, related,
          when: c.last_message_at,
        });
      }
    });
    // Open asks first (awaiting), then by deadline (dated before undated), newest last-touch as tiebreak.
    commitments.sort((a, b) => (b.awaiting - a.awaiting)
      || ((a.deadlineDate || "9999") < (b.deadlineDate || "9999") ? -1 : 1));
    // Our promises: overdue first, then soonest deadline.
    promises.sort((a, b) => (b.overdue - a.overdue) || ((a.deadlineDate || "9999") < (b.deadlineDate || "9999") ? -1 : 1));
    unresolved.sort((a, b) => (new Date(b.when) - new Date(a.when)));
    // Longest-waiting clients first — those are the ones to chase.
    waitingOnClient.sort((a, b) => b.days - a.days);
    slipping.sort((a, b) => b.slips - a.slips);

    const perPerson = Object.values(person).map((p) => ({
      name: p.name, email: p.email, replies: p.replies, threads: p.threads.size,
      avgRespMins: p.resp.length ? Math.round(median(p.resp) / 60000) : null, awaiting: p.awaiting,
    })).sort((a, b) => b.replies - a.replies).slice(0, 10);

    const highEffort = effort.sort((a, b) => b.count - a.count).slice(0, 6);

    return ok({
      scope: empty.scope, // the Radar mailboxes, resolved above
      kpis: {
        total: rows.length, responded, responseRate: withExt ? Math.round((responded / withExt) * 100) : 0,
        medianFirstMins: Math.round(median(firstRespMs) / 60000), avgFirstMins: firstRespMs.length ? Math.round((firstRespMs.reduce((a, b) => a + b, 0) / firstRespMs.length) / 60000) : 0,
        awaiting: rows.filter((c) => c.awaiting_reply).length, negative: sentiment.negative,
      },
      aging, slaBreach, volumeByDay, heatmap: heat, channels: { email: rows.length, whatsapp, calls },
      perPerson, unassignedAwaiting, sentiment, sentimentTrend, sentimentByCampaign, topCampaigns, goingQuiet, highEffort,
      topics, riskCounts, commitments: commitments.slice(0, 30),
      promises: promises.slice(0, 30), unresolved: unresolved.slice(0, 20),
      promisesOverdue: promises.filter((p) => p.overdue).length,
      // reliability = of the promises that came due, how many we actually kept
      reliability: { kept, broken, rate: (kept + broken) ? Math.round((kept / (kept + broken)) * 100) : null },
      blockerCounts, slipping: slipping.slice(0, 10),
      missingAttachment: missingAttachment.slice(0, 10),
      waitingOnClient: waitingOnClient.slice(0, 20),
      needsChase: waitingOnClient.filter((w) => w.needsChase).length,
    });
  },

  // Dismiss ONE conversation from the awaiting list (it stays dismissed even if
  // the client writes again — use unmute to bring it back).
  async dismissAwaiting(conversationId) {
    const { error } = await supabase.from("pulse_conversation")
      .update({ awaiting_muted: true, awaiting_reply: false }).eq("id", conversationId);
    return { data: null, error: error ? error.message : null };
  },
  async undismissAwaiting(conversationId) {
    const { error } = await supabase.from("pulse_conversation")
      .update({ awaiting_muted: false }).eq("id", conversationId);
    return { data: null, error: error ? error.message : null };
  },

  // Mute a whole sender (address) or their domain — hides current + future
  // threads from the awaiting list. `scope` = "address" | "domain".
  async muteSender(emailAddress, scope = "address", reason = "") {
    const addr = String(emailAddress || "").trim().toLowerCase();
    if (!addr.includes("@")) return { data: null, error: "Not a valid email address" };
    const domain = addr.split("@")[1];
    const { data: auth } = await supabase.auth.getUser();
    const row = scope === "domain"
      ? { domain, reason, created_by: auth?.user?.id || null }
      : { address: addr, reason, created_by: auth?.user?.id || null };
    const { error } = await supabase.from("pulse_muted_sender").insert(row);
    if (error && error.code !== "23505") return { data: null, error: error.message }; // 23505 = already muted
    // Hide anything already ingested from this sender.
    let q = supabase.from("pulse_conversation").update({ awaiting_muted: true, awaiting_reply: false }).eq("channel", "email");
    q = scope === "domain" ? q.ilike("from_address", `%@${domain}`) : q.ilike("from_address", addr);
    await q;
    return ok(true);
  },

  async getMutedSenders() {
    const { data, error } = await supabase.from("pulse_muted_sender")
      .select("id, address, domain, reason, created_at").order("created_at", { ascending: false });
    if (error) return { data: [], error: error.message };
    return ok(data || []);
  },

  // Remove a mute and un-hide that sender's threads so they can surface again.
  async unmuteSender(id) {
    const { data: row } = await supabase.from("pulse_muted_sender").select("address, domain").eq("id", id).maybeSingle();
    const { error } = await supabase.from("pulse_muted_sender").delete().eq("id", id);
    if (error) return { data: null, error: error.message };
    if (row) {
      let q = supabase.from("pulse_conversation").update({ awaiting_muted: false }).eq("channel", "email");
      q = row.domain ? q.ilike("from_address", `%@${row.domain}`) : q.ilike("from_address", row.address);
      await q;
    }
    return ok(true);
  },

  // Client emails by sentiment ("negative" | "positive"), newest first,
  // paginated (Radar tables). Returns { rows, total } so the UI can page.
  async getNegativeEmails(page = 0, perPage = 10, sentiment = "negative") {
    const from = page * perPage;
    const { data, error, count } = await supabase.from("pulse_conversation")
      .select("id, from_name, from_address, subject, last_message_at, campaign_id, awaiting_reply, mailbox:channel_account_id(identifier)", { count: "exact" })
      .eq("channel", "email").eq("sentiment", sentiment)
      .order("last_message_at", { ascending: false })
      .range(from, from + perPage - 1);
    if (error) return { data: { rows: [], total: 0 }, error: error.message };
    // The conversation's from_address can be one of OUR addresses (group-copy
    // inbox), so resolve the real EXTERNAL client from the thread's messages.
    const ids = (data || []).map((c) => c.id);
    const client = {};
    const owner = {};  // last colleague who WROTE TO THE CLIENT — who to ask
    // Group/inbox addresses are "us" but not a person — never show them as owner.
    if (ids.length) {
      // Paged + oldest-first, so walk it in reverse to get "newest wins".
      const msgs = await _messagesForConversations(ids, "conversation_id, from_name, from_address, to_addresses, cc_addresses, sent_at");
      for (let i = msgs.length - 1; i >= 0; i--) {
        const m = msgs[i];
        const cid = m.conversation_id;
        const f = (m.from_address || "").toLowerCase();
        // Relayed client mail carries OUR group address — treat it as the client,
        // otherwise this row shows "Campaigns" as the sender and no real client.
        const internal = isInternalAddress(f) && !_isRelayedClient(m);
        if (!client[cid] && !internal) client[cid] = m;
        // Must have written TO THE CLIENT — same guard as the Awaiting-response
        // Responsible column. A colleague who only sent an internal note ("can you
        // look at this?") is not who to ask about the client relationship, and
        // naming them reads as "this person handled it" when nobody has.
        const wroteToClient = [...(m.to_addresses || []), ...(m.cc_addresses || [])]
          .map((x) => String(x || "").toLowerCase())
          .some((a) => a.includes("@") && !isInternalAddress(a));
        if (!owner[cid] && internal && !isGroupAddress(f) && wroteToClient) owner[cid] = m;
      }
    }
    const rows = (data || []).map((c) => {
      const src = client[c.id] || { from_name: c.from_name, from_address: c.from_address };
      const p = _parseAddr(src.from_address || "");
      const o = owner[c.id];
      const op = o ? _parseAddr(o.from_address || "") : null;
      return {
        id: c.id,
        fromName: _isRelayedClient(src)
          ? _decode(_relayAuthor(src))
          : (_decode(src.from_name || "") || p.name || p.email),
        fromEmail: p.email,
        subject: _decode(c.subject) || "(no subject)",
        when: c.last_message_at,
        campaignId: c.campaign_id,
        awaiting: !!c.awaiting_reply,
        mailbox: c.mailbox?.identifier || "",
        owner: o ? (_decode(o.from_name || "") || op.name || op.email) : "",
      };
    });
    return ok({ rows, total: count || 0 });
  },

  // Summarise a whole thread (on demand, cached server-side). `force` regenerates
  // after new mail has arrived.
  async summarizeThread(conversationId, force = false) {
    const { data, error } = await supabase.functions.invoke("pulse-summarize-thread", {
      body: { conversationId, force },
    });
    if (error) return { data: null, error: error.message || "Couldn't summarise" };
    if (data && data.error) return { data: null, error: data.error };
    return ok(data);
  },

  // ---- Radar notifications --------------------------------------------------
  // Rows are written ONLY by the edge functions (service role). The browser can
  // read its own and mark them read — it cannot create one, by design, so an alert
  // always corresponds to something the analyser or the sweep actually observed.
  // RLS scopes reads to the current user (managers see all), so no filter here.
  async getNotifications({ limit = 40, unreadOnly = false } = {}) {
    let q = supabase.from("pulse_notification")
      .select("id, conversation_id, channel_account_id, kind, severity, title, body, for_name, escalated_at, read_at, created_at")
      .order("created_at", { ascending: false })
      .limit(limit);
    if (unreadOnly) q = q.is("read_at", null);
    const { data, error } = await q;
    if (error) return { data: [], error: error.message };
    return ok((data || []).map((n) => ({
      id: n.id,
      convId: n.conversation_id,        // null for mailbox-health alerts
      accountId: n.channel_account_id,
      kind: n.kind,
      severity: n.severity,
      title: _decode(n.title || ""),
      body: _decode(n.body || ""),
      owner: n.for_name || "",
      escalated: !!n.escalated_at,
      read: !!n.read_at,
      when: n.created_at,
    })));
  },

  async getNotificationUnreadCount() {
    const { count, error } = await supabase.from("pulse_notification")
      .select("id", { count: "exact", head: true }).is("read_at", null);
    if (error) return { data: 0, error: error.message };
    return ok(count || 0);
  },

  async markNotificationRead(id, read = true) {
    const { data: uid } = await this.getMyUserId();
    const { error } = await supabase.from("pulse_notification")
      .update(read ? { read_at: new Date().toISOString(), read_by: uid || null }
                   : { read_at: null, read_by: null })
      .eq("id", id);
    if (error) return { data: null, error: error.message };
    return ok(true);
  },

  // Marks every notification the CURRENT USER can see as read. For a manager that
  // is everyone's — which is what "mark all read" means from their own bell, and
  // it does not touch anyone else's read state because the rows are per-recipient.
  async markAllNotificationsRead() {
    const { data: uid } = await this.getMyUserId();
    const { error } = await supabase.from("pulse_notification")
      .update({ read_at: new Date().toISOString(), read_by: uid || null })
      .is("read_at", null);
    if (error) return { data: null, error: error.message };
    return ok(true);
  },

  async getNotificationPrefs() {
    const { data: uid } = await this.getMyUserId();
    if (!uid) return ok(null);
    const { data } = await supabase.from("pulse_notification_pref")
      .select("escalation, negative, overdue, unanswered, slipped, unanswered_hours")
      .eq("user_id", uid).maybeSingle();
    // No row = defaults, and the defaults are ON — nobody should have to opt in to
    // hearing that a client is escalating.
    return ok(data || { escalation: true, negative: true, overdue: true, unanswered: true, slipped: true, unanswered_hours: 48 });
  },

  async saveNotificationPrefs(prefs) {
    const { data: uid } = await this.getMyUserId();
    if (!uid) return { data: null, error: "Not signed in" };
    const { error } = await supabase.from("pulse_notification_pref").upsert({
      user_id: uid,
      escalation: prefs.escalation !== false,
      negative: prefs.negative !== false,
      overdue: prefs.overdue !== false,
      unanswered: prefs.unanswered !== false,
      slipped: prefs.slipped !== false,
      unanswered_hours: Math.min(720, Math.max(1, Number(prefs.unanswered_hours) || 48)),
      updated_at: new Date().toISOString(),
    }, { onConflict: "user_id" });
    if (error) return { data: null, error: error.message };
    return ok(true);
  },

  // ---- manual resolve (Radar) ----------------------------------------------
  // The AI only sees email, so work closed on a call / WhatsApp / in a dashboard
  // still shows as open. These let a person settle it. Stamped with who + when so
  // it's auditable and undoable, and the override lapses if the client writes
  // again (see getInsightsAnalytics — a newer client message revives the row).
  async markAskResolved(conversationId, done = true) {
    const { data: uid } = await this.getMyUserId();
    const { error } = await supabase.from("pulse_conversation")
      .update(done ? { ask_resolved_at: new Date().toISOString(), ask_resolved_by: uid || null }
                   : { ask_resolved_at: null, ask_resolved_by: null })
      .eq("id", conversationId);
    return error ? { data: false, error: error.message } : ok(true);
  },
  async markPromiseDone(conversationId, done = true) {
    const { data: uid } = await this.getMyUserId();
    const { error } = await supabase.from("pulse_conversation")
      .update(done ? { promise_done_at: new Date().toISOString(), promise_done_by: uid || null }
                   : { promise_done_at: null, promise_done_by: null })
      .eq("id", conversationId);
    return error ? { data: false, error: error.message } : ok(true);
  },

  // Remove / restore a thread on Radar's "Waiting on client" board. Same lapse
  // rule as the other overrides: a newer message on the thread revives it.
  async markWaitingCleared(conversationId, cleared = true) {
    const { data: uid } = await this.getMyUserId();
    const { error } = await supabase.from("pulse_conversation")
      .update(cleared ? { waiting_cleared_at: new Date().toISOString(), waiting_cleared_by: uid || null }
                      : { waiting_cleared_at: null, waiting_cleared_by: null })
      .eq("id", conversationId);
    return error ? { data: false, error: error.message } : ok(true);
  },

  // Fetch a single conversation as a thread (for deep-linking Insights → Email).
  async getConversationThread(id) {
    const { data } = await supabase.from("pulse_conversation").select(_CONV_SELECT_LIST).eq("id", id).maybeSingle();
    if (!data) return ok(null);
    const avatars = await this._contactAvatarMap();
    const t = _thread(data, avatars, false);
    await _enrichGravatars([t]);
    return ok(t);
  },

  // Autocomplete search over the real campaign table (by title). Returns
  // {id,label,color} for the assign-panel picker.
  async searchCampaigns(query, limit = 20) {
    const term = (query || "").trim();
    if (term.length < 2) return ok([]);
    const { data, error } = await supabase
      .from("campaign")
      .select("id, title, is_active, brand:brand_id(brand_name, company:company_id(company_name))")
      .ilike("title", `%${term}%`)
      .order("is_active", { ascending: false })
      .order("id", { ascending: false })
      .limit(limit);
    if (error || !data) return ok([]);
    const palette = ["#db2777", "#b45309", "#0369a1", "#7c3aed", "#059669", "#dc2626", "#0891b2", "#c026d3", "#ea580c", "#4338ca"];
    return ok(data.map((c) => {
      const brand = c.brand?.brand_name || c.brand?.company?.company_name || "";
      return {
        id: c.id,
        label: brand ? `${brand} — ${c.title || "Campaign #" + c.id}` : (c.title || "Campaign #" + c.id),
        color: palette[Number(c.id) % palette.length],
        active: c.is_active === true,
      };
    }));
  },

  // Resolve specific campaign ids to {id,label,color} — used to label detected
  // campaigns that fall outside the recent-campaigns list loaded for the picker.
  async getCampaignsByIds(ids) {
    const list = [...new Set((ids || []).filter(Boolean))];
    if (!list.length) return ok([]);
    const { data, error } = await supabase
      .from("campaign")
      .select("id, title, is_active, brand:brand_id(brand_name, company:company_id(company_name))")
      .in("id", list);
    if (error || !data) return ok([]);
    const palette = ["#db2777", "#b45309", "#0369a1", "#7c3aed", "#059669", "#dc2626", "#0891b2", "#c026d3", "#ea580c", "#4338ca"];
    return ok(data.map((c) => {
      const brand = c.brand?.brand_name || c.brand?.company?.company_name || "";
      return {
        id: c.id,
        label: brand ? `${brand} — ${c.title || "Campaign #" + c.id}` : (c.title || "Campaign #" + c.id),
        color: palette[Number(c.id) % palette.length],
        active: c.is_active === true,
      };
    }));
  },

  // Re-run campaign auto-detection over history (live mail is tagged on ingest).
  // Optionally scoped to one mailbox. Returns { assigned, scanned }.
  async rescanCampaigns(mailboxId) {
    const { data, error } = await supabase.functions.invoke("pulse-classify-campaign", { body: mailboxId ? { mailboxId } : {} });
    if (error) return { data: null, error: (data && data.error) || error.message };
    if (data && data.error) return { data: null, error: data.error };
    return ok(data);
  },

  // Backfill client-email sentiment over history (live mail is scored on ingest).
  async rescanSentiment(mailboxId) {
    const { data, error } = await supabase.functions.invoke("pulse-classify-sentiment", { body: mailboxId ? { mailboxId } : {} });
    if (error) return { data: null, error: (data && data.error) || error.message };
    if (data && data.error) return { data: null, error: data.error };
    return ok(data);
  },

  async getOverview() { return ok(mock.OVERVIEW); },

  // ==================================================================
  // ADMIN (Supabase-backed)
  // ==================================================================
  accessFeatures: mock.ACCESS_FEATURES,
  accessLevels: mock.ACCESS_LEVELS,
  mailboxKind: mock.MAILBOX_KIND,

  // Current user's Pulse permissions (null = not a member → no access).
  async getMyPermissions() {
    try {
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth?.user?.id;
      if (!uid) return { data: null, error: "not signed in" };
      const { data, error } = await supabase.from("pulse_member").select("*").eq("user_id", uid).maybeSingle();
      if (error) return { data: null, error: error.message };
      return ok(data);
    } catch (e) { return { data: null, error: String(e) }; }
  },

  // Current signed-in staff member's id + display name (for attribution + typing).
  async getMyIdentity() {
    try {
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth?.user?.id;
      if (!uid) return { data: null, error: "not signed in" };
      const { data } = await supabase.from("pulse_member")
        .select("user:user_id(first_name, last_name, email)").eq("user_id", uid).maybeSingle();
      const u = (data && data.user) || {};
      const name = [u.first_name, u.last_name].filter(Boolean).join(" ").trim() || u.email || "You";
      return ok({ id: uid, name });
    } catch (e) { return { data: null, error: String(e) }; }
  },

  // All members + user info → normalised to the view shape.
  async getMembers() {
    const { data, error } = await supabase
      .from("pulse_member")
      .select("user_id, is_admin, phone, email_access, phone_access, whatsapp_access, calendar_access, user:user_id(first_name, last_name, email, avatar)")
      .order("created_at", { ascending: true });
    if (error) return { data: [], error: error.message };
    return ok((data || []).map(_member));
  },

  // Staff users not yet Pulse members (add-member picker).
  async searchStaffUsers(query, excludeIds = []) {
    const term = (query || "").trim();
    if (!term) return ok([]);
    const { data, error } = await supabase
      .from("users")
      .select("id, first_name, last_name, email, avatar")
      .eq("user_type", "staff").eq("deactivate", false)
      .or(`first_name.ilike.%${term}%,last_name.ilike.%${term}%,email.ilike.%${term}%`)
      .limit(8);
    if (error) return { data: [], error: error.message };
    return ok((data || []).filter((u) => !excludeIds.includes(u.id)).map(_staff));
  },

  async addMember(userId) {
    const { error } = await supabase.from("pulse_member").insert({
      user_id: userId, is_admin: false, email_access: "own", phone_access: "none", whatsapp_access: "own", calendar_access: "own",
    });
    return { data: null, error: error ? (error.code === "23505" ? "Already a member" : error.message) : null };
  },

  async saveMemberAccess(member) {
    // Phone is stored digits-only with country code ("+971 50 123 4567" → 971501234567)
    // so the listener webhook can match it against group participants.
    const digits = String(member.phone || "").replace(/\D/g, "").replace(/^00/, "");
    const patch = {
      is_admin: !!member.admin,
      phone: digits || null,
      email_access: member.access.email, phone_access: member.access.phone,
      whatsapp_access: member.access.whatsapp, calendar_access: member.access.calendar,
      updated_at: new Date().toISOString(),
    };
    const { error } = await supabase.from("pulse_member").update(patch).eq("user_id", member.id);
    return { data: null, error: error ? error.message : null };
  },

  async removeMember(userId) {
    const { error } = await supabase.from("pulse_member").delete().eq("user_id", userId);
    return { data: null, error: error ? error.message : null };
  },

  // Mailboxes (channel='email') grouped by org, with assigned member ids.
  async getMailboxGroups() {
    const [{ data: accts, error: e1 }, { data: access, error: e2 }] = await Promise.all([
      supabase.from("pulse_channel_account").select("id, label, identifier, kind, org, domain, status, radar, watch_expiration, sync_status, last_error, last_synced_at").eq("channel", "email").order("id", { ascending: true }),
      supabase.from("pulse_mailbox_access").select("channel_account_id, user_id"),
    ]);
    if (e1) return { data: [], error: e1.message };
    if (e2) return { data: [], error: e2.message };
    const byAcct = {};
    (access || []).forEach((r) => {
      if (!byAcct[r.channel_account_id]) byAcct[r.channel_account_id] = [];
      byAcct[r.channel_account_id].push(r.user_id);
    });
    const groups = {};
    (accts || []).forEach((a) => {
      const org = a.org || "Other";
      if (!groups[org]) groups[org] = { org, domain: a.domain || "", note: _ORG_NOTE[org] || "Connected mailboxes.", accounts: [] };
      groups[org].accounts.push({
        id: a.id, address: a.identifier, label: a.label || a.identifier,
        kind: a.kind || "personal", status: a.status || "connected", assigned: byAcct[a.id] || [],
        watchExpiration: a.watch_expiration || null, syncStatus: a.sync_status || null,
        lastError: a.last_error || null, lastSyncedAt: a.last_synced_at || null,
        radar: !!a.radar,
      });
    });
    return ok(Object.values(groups));
  },

  // Turn Radar on/off for a mailbox. Radar analyses every new client message with
  // an LLM, so this is a cost + noise decision, not a display preference — which is
  // why it is an explicit toggle rather than something connecting a mailbox implies.
  async setMailboxRadar(id, on) {
    const { error } = await supabase.from("pulse_channel_account").update({ radar: !!on }).eq("id", id);
    if (error) return { data: null, error: error.message };
    _radarMbs = null; // the cached set is now stale
    return ok(true);
  },

  async addMailbox({ address, label, kind = "personal", org = "Brandripplr", domain }) {
    const dom = domain || (address.includes("@") ? address.split("@")[1] : "");
    const { data: auth } = await supabase.auth.getUser();
    const { data, error } = await supabase.from("pulse_channel_account")
      .insert({ channel: "email", identifier: address, label, kind, org, domain: dom, status: "connected", created_by: auth?.user?.id || null })
      .select("id").single();
    if (error) return { data: null, error: error.message };
    // Auto-start ingestion for the freshly connected mailbox: mark it running so
    // the Email screen shows progress immediately, kick off the full backfill,
    // and register the Gmail watch so live mail flows right away.
    await supabase.from("pulse_channel_account").update({ backfill_status: "running", backfill_imported: 0, backfill_total: null }).eq("id", data.id);
    this.startBackfill(data.id).catch(() => {});
    this.triggerWatch().catch(() => {});
    return ok(data);
  },

  // -- WhatsApp Business numbers (channel='whatsapp') --------------------------
  // Cloud API numbers only by default: every caller except the Admin tab is a
  // "send from" picker (new chat, group create, templates), and the WasenderAPI
  // listener can't send. Admin passes { includeListener: true } to show it.
  async getWhatsappNumbers({ includeListener = false } = {}) {
    const [{ data, error }, { data: access }] = await Promise.all([
      supabase.from("pulse_channel_account")
        .select("id, label, identifier, external_account_id, status, meta, created_at, provider, sync_status, last_error, last_synced_at, is_active, radar")
        .eq("channel", "whatsapp").order("id", { ascending: true }),
      // Same table as mailboxes: pulse_mailbox_access is per channel account, and
      // the conversation RLS already honours it for WhatsApp ("Own" members see
      // only the numbers granted here; "Team"/"Full" see every number).
      supabase.from("pulse_mailbox_access").select("channel_account_id, user_id"),
    ]);
    if (error) return { data: [], error: error.message };
    const byAcct = {};
    (access || []).forEach((r) => {
      if (!byAcct[r.channel_account_id]) byAcct[r.channel_account_id] = [];
      byAcct[r.channel_account_id].push(r.user_id);
    });
    return ok((data || []).filter((a) => includeListener || a.provider !== "wasender").map((a) => {
      const m = a.meta || {};
      return {
        id: a.id,
        assigned: byAcct[a.id] || [],
        // The access modal shows label + address, same shape as a mailbox row.
        address: a.identifier ? "+" + String(a.identifier).replace(/\D/g, "") : "",
        number: a.identifier,
        label: a.label || m.display_name || a.identifier,
        displayName: m.display_name || a.label || "",
        phoneNumberId: a.external_account_id || "",
        wabaId: m.waba_id || "",
        category: m.category || "",
        about: m.about || "",
        ownerTeam: m.owner_team || "",
        status: a.status || "connected",
        radar: !!a.radar,   // listener: Radar reads its client groups (asks, promises, sentiment)
        connectedAt: a.created_at,
        provider: a.provider || "meta",
        listener: a.provider === "wasender",
        // Personal listener = a team member's own phone: only groups an admin
        // switched on are kept (see pulse-wasender-admin listGroups / watchGroup).
        personal: !!m.personal,
        apiKeyEnv: m.api_key_env || "WASENDER_API_KEY",
        secretEnv: m.webhook_secret_env || "WASENDER_WEBHOOK_SECRET",
        syncStatus: a.sync_status || "",
        lastError: a.last_error || "",
        lastSeen: a.last_synced_at ? _whenFull(a.last_synced_at) : "",
        active: a.is_active !== false,
      };
    }));
  },
  // WhatsApp numbers explicitly granted to the current user (Chats "Mine" scope).
  // Admins / Team / Full members see every number through RLS anyway, so for
  // them "Mine" is only a filter, never a permission.
  async getMyWhatsappNumberIds() {
    const uid = await _uid();
    if (!uid) return ok([]);
    const { data, error } = await supabase.from("pulse_mailbox_access")
      .select("channel_account_id, account:channel_account_id(channel)").eq("user_id", uid);
    if (error) return { data: [], error: error.message };
    return ok((data || []).filter((r) => r.account && r.account.channel === "whatsapp").map((r) => r.channel_account_id));
  },

  // WasenderAPI listener number (Admin → WhatsApp → Listener card).
  // op: "status" | "connect" | "syncGroups" | "groupMeta"
  async wasenderAdmin(op, extra = {}) {
    const { data, error } = await supabase.functions.invoke("pulse-wasender-admin", { body: { op, ...extra } });
    if (error) {
      // A non-2xx reply carries the function's JSON in the FunctionsHttpError context.
      let msg = error.message;
      try { const j = await error.context?.json?.(); if (j?.error) msg = j.error; } catch { /* keep message */ }
      return { data: null, error: (data && data.error) || msg };
    }
    if (data && data.error) return { data: null, error: data.error };
    return ok(data);
  },

  async addWhatsappNumber(n) {
    const { data: auth } = await supabase.auth.getUser();
    // Find an existing WhatsApp row for this number (by phone-number-id, else by
    // the number itself) so re-connecting UPDATES it in place — e.g. to add a
    // WABA ID or phone-number-id — instead of silently creating a duplicate row.
    let existing = null;
    if (n.phoneNumberId) {
      const { data } = await supabase.from("pulse_channel_account")
        .select("id, meta").eq("channel", "whatsapp").eq("external_account_id", n.phoneNumberId).maybeSingle();
      existing = data || null;
    }
    if (!existing && n.number) {
      const { data } = await supabase.from("pulse_channel_account")
        .select("id, meta").eq("channel", "whatsapp").eq("identifier", n.number).maybeSingle();
      existing = data || null;
    }
    const prev = (existing && existing.meta) || {};
    const meta = {
      ...prev,
      waba_id: n.wabaId || prev.waba_id || "",
      display_name: n.displayName || prev.display_name || "",
      category: n.category || prev.category || "",
      about: n.about || prev.about || "",
      owner_team: n.ownerTeam || prev.owner_team || "",
      mode: n.mode || prev.mode || "new",
      import_mode: n.importMode || prev.import_mode || "skip",
    };
    if (existing) {
      const patch = { status: "connected", meta };
      if (n.phoneNumberId) patch.external_account_id = n.phoneNumberId;
      if (n.label || n.displayName) patch.label = n.label || n.displayName;
      const { data, error } = await supabase.from("pulse_channel_account")
        .update(patch).eq("id", existing.id).select("id").single();
      if (error) return { data: null, error: error.message };
      return ok(data);
    }
    const { data, error } = await supabase.from("pulse_channel_account")
      .insert({
        channel: "whatsapp",
        identifier: n.number,
        label: n.label || n.displayName || n.number,
        external_account_id: n.phoneNumberId || null,
        status: "connected",
        meta,
        created_by: auth?.user?.id || null,
      })
      .select("id").single();
    if (error) return { data: null, error: error.message };
    return ok(data);
  },
  async removeWhatsappNumber(id) {
    const { error } = await supabase.from("pulse_channel_account").delete().eq("id", id);
    return { data: null, error: error ? error.message : null };
  },
  // Complete WhatsApp Embedded Signup: hand the ES authorization code + ids to
  // the backend, which exchanges the token, subscribes webhooks, registers the
  // number and stores its per-WABA credential. Returns the new channel account.
  async embeddedSignup({ code, wabaId, phoneNumberId }) {
    const { data, error } = await supabase.functions.invoke("pulse-whatsapp-embedded-signup", {
      body: { code, wabaId, phoneNumberId },
    });
    if (error) return { data: null, error: (data && data.error) || error.message };
    if (data && data.error) return { data: null, error: data.error };
    return ok(data);
  },
  // Register/activate a number on the Cloud API (moves it from Pending → Connected).
  async registerWhatsappNumber({ phoneNumberId, pin }) {
    const { data, error } = await supabase.functions.invoke("pulse-whatsapp-register", { body: { phoneNumberId, pin } });
    if (error) return { data: null, error: error.message };
    if (data && data.success) return ok(data);
    return { data: null, error: (data && data.error) || "Registration failed" };
  },
  // Read a number's WhatsApp business profile (about, address, websites, photo…).
  async getWhatsappProfile(channelAccountId) {
    const { data, error } = await supabase.functions.invoke("pulse-whatsapp-profile", { body: { channelAccountId, action: "get" } });
    if (error) return { data: null, error: error.message };
    if (data && data.success) return ok(data.profile || {});
    return { data: null, error: (data && data.error) || "Couldn't load profile" };
  },
  // Update the business profile. `fields` = { about, address, description, email, vertical, websites[] }.
  // Optional `picture` (a File) is uploaded to storage, then Meta's resumable upload.
  async updateWhatsappProfile(channelAccountId, fields, picture) {
    try {
      let picturePath = "", pictureMime = "";
      if (picture) {
        const ext = picture.type === "image/png" ? "png" : "jpg";
        picturePath = `profile/${channelAccountId}-${Date.now()}.${ext}`;
        pictureMime = picture.type || "image/jpeg";
        const up = await supabase.storage.from("pulse-wa-media").upload(picturePath, picture, { contentType: pictureMime, upsert: true });
        if (up.error) return { data: null, error: up.error.message };
      }
      const { data, error } = await supabase.functions.invoke("pulse-whatsapp-profile", {
        body: { channelAccountId, action: "update", fields, picturePath, pictureMime },
      });
      if (error) return { data: null, error: error.message };
      if (data && data.success) return ok(data.profile || {});
      return { data: null, error: (data && data.error) || "Update failed" };
    } catch (e) { return { data: null, error: String(e) }; }
  },

  // Register/renew the Gmail watch across mailboxes (also runs on a 6-hourly cron).
  async triggerWatch() {
    const { data, error } = await supabase.functions.invoke("pulse-gmail-watch", {});
    if (error) return { data: null, error: error.message };
    return ok(data);
  },

  // Aggregate backfill progress across the user's email mailboxes (for the
  // Email screen's import progress bar).
  async getBackfillProgress() {
    try {
      const { data } = await supabase.from("pulse_channel_account")
        .select("id, label, identifier, backfill_status, backfill_imported, backfill_total")
        .eq("channel", "email");
      const boxes = (data || []).map((a) => {
        const total = a.backfill_total || 0;
        const imported = a.backfill_imported || 0;
        // done → 100 (the profile total counts trash/spam we skip, so scanned
        // never exactly hits it); otherwise cap the live fraction at 99%.
        const pct = a.backfill_status === "done" ? 100 : (total ? Math.min(99, Math.round((100 * imported) / total)) : 0);
        return { id: a.id, label: a.label || a.identifier, status: a.backfill_status, imported, total, pct };
      });
      // "importing" is an active-batch lock — treat it as running for the UI.
      const running = boxes.some((b) => b.status === "running" || b.status === "importing");
      const imported = boxes.reduce((s, b) => s + b.imported, 0);
      const total = boxes.reduce((s, b) => s + b.total, 0);
      const pct = !running ? 100 : (total ? Math.min(99, Math.round((100 * imported) / total)) : 0);
      return ok({ running, imported, total, pct, boxes });
    } catch { return ok({ running: false, imported: 0, total: 0, pct: 100, boxes: [] }); }
  },

  // Rename / retype a mailbox (address is the Gmail identity — not editable here).
  async updateMailbox(id, { label, kind, org }) {
    const patch = {};
    if (label !== undefined) patch.label = label;
    if (kind !== undefined) patch.kind = kind;
    if (org !== undefined) patch.org = org;
    const { error } = await supabase.from("pulse_channel_account").update(patch).eq("id", id);
    return { data: null, error: error ? error.message : null };
  },

  async removeMailbox(id) {
    const { error } = await supabase.from("pulse_channel_account").delete().eq("id", id);
    if (error) return { data: null, error: error.code === "23503" ? "This mailbox has conversations — can't delete. It stays connected." : error.message };
    return ok(true);
  },

  // Replace a mailbox's access list with the given member ids.
  async saveMailboxAssignment(mailboxId, userIds) {
    const { error: delErr } = await supabase.from("pulse_mailbox_access").delete().eq("channel_account_id", mailboxId);
    if (delErr) return { data: null, error: delErr.message };
    if (userIds.length) {
      const rows = userIds.map((uid) => ({ channel_account_id: mailboxId, user_id: uid }));
      const { error } = await supabase.from("pulse_mailbox_access").insert(rows);
      if (error) return { data: null, error: error.message };
    }
    return ok(true);
  },

  // Current user's signature fields (designation + phone come from Admin settings).
  async getMySignature() {
    const uid = await _uid();
    if (!uid) return ok({ name: "", email: "", role: "", phone: "", website: "brandripplr.com", address: "" });
    const [{ data: sig }, { data: u }] = await Promise.all([
      supabase.from("pulse_signature").select("role, phone, website, address").eq("user_id", uid).maybeSingle(),
      supabase.from("users").select("first_name, last_name, email").eq("id", uid).maybeSingle(),
    ]);
    return ok({
      name: _fullName(u), email: u?.email || "",
      role: sig?.role || "", phone: sig?.phone || "",
      website: sig?.website || "brandripplr.com", address: sig?.address || "",
    });
  },

  async getSignatures() {
    const { data, error } = await supabase
      .from("pulse_signature")
      .select("user_id, role, phone, website, address, user:user_id(first_name, last_name)");
    if (error) return { data: [], error: error.message };
    return ok((data || []).map((s) => ({
      userId: s.user_id, name: _fullName(s.user), role: s.role, phone: s.phone, website: s.website, address: s.address,
    })));
  },

  async saveSignature(sig) {
    const { error } = await supabase.from("pulse_signature").upsert({
      user_id: sig.userId, role: sig.role, phone: sig.phone, website: sig.website, address: sig.address, updated_at: new Date().toISOString(),
    });
    return { data: null, error: error ? error.message : null };
  },

  // ==================================================================
  // EMAIL (data layer — DB-first, mock fallback until Gmail ingestion)
  // ==================================================================
  async getEmail(limit = 100, folder = "inbox", withCounts = true, filter = null, mailboxId = null) {
    const base = { folders: mock.MAIL_FOLDERS, labels: mock.MAIL_LABELS, savedReplies: mock.SAVED_REPLIES };
    const SELECT = _CONV_SELECT_LIST;
    // When a specific mailbox is selected, scope the query to its channel account.
    const withMb = (q) => (mailboxId ? q.eq("channel_account_id", mailboxId) : q);
    try {
      let data;
      let error;
      if (folder && folder.startsWith("label:")) {
        // Custom-label folders: `label_ids @> {X}` can't use the GIN index under
        // RLS (non-leakproof operator), so fetch matching ids via a SECURITY
        // DEFINER RPC (indexed + visibility-checked), then load those rows.
        const labelId = folder.slice(6);
        const { data: idRows, error: e1 } = await supabase.rpc("pulse_label_conversation_ids", { label_id: labelId, lim: limit });
        if (e1) return ok({ ...base, threads: [], demo: false, counts: {} });
        const top = (idRows || []).map((r) => r.id);
        if (!top.length) return ok({ ...base, threads: [], demo: false, counts: withCounts ? await this._folderCounts(mailboxId) : undefined });
        const r = await _applyFilter(withMb(supabase.from("pulse_conversation").select(SELECT).in("id", top)), filter);
        data = r.data; error = r.error;
        if (data) data.sort((a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0));
      } else {
        const q = _applyFilter(_applyFolder(withMb(supabase.from("pulse_conversation").select(SELECT).eq("channel", "email")), folder), filter);
        const r = await q.order("last_message_at", { ascending: false }).limit(limit);
        data = r.data; error = r.error;
      }
      if (error) return ok({ ...base, threads: folder === "inbox" ? mock.EMAIL_THREADS : [], demo: folder === "inbox", counts: {} });
      if (!data || !data.length) {
        // fall back to the sample inbox only before ANYTHING is ingested — never
        // when a specific mailbox is selected (an empty mailbox stays empty).
        if (folder === "inbox" && !mailboxId) {
          const { count } = await supabase.from("pulse_conversation").select("id", { count: "exact", head: true }).eq("channel", "email");
          if (!count) return ok({ ...base, threads: mock.EMAIL_THREADS, demo: true, counts: {} });
        }
        return ok({ ...base, threads: [], demo: false, counts: withCounts ? await this._folderCounts(mailboxId) : undefined });
      }
      const avatars = await this._contactAvatarMap();
      const threads = data.map((c) => _thread(c, avatars, false));
      await _enrichGravatars(threads);
      const counts = withCounts ? await this._folderCounts(mailboxId) : undefined;
      const folders = counts ? mock.MAIL_FOLDERS.map((f) => ({ ...f, count: counts[f.key] || 0 })) : mock.MAIL_FOLDERS;
      return ok({ ...base, folders, threads, counts, demo: false });
    } catch {
      return ok({ ...base, threads: folder === "inbox" ? mock.EMAIL_THREADS : [], demo: folder === "inbox", counts: {} });
    }
  },

  // Full message bodies/html for ONE conversation — fetched lazily when a thread
  // is opened (the list fetch omits them for speed).
  async getThreadMessages(conversationId) {
    if (!conversationId) return ok([]);
    const { data } = await supabase.from("pulse_message")
      .select("id, direction, from_name, from_address, to_addresses, cc_addresses, bcc_addresses, external_message_id, body, html, label_ids, snippet, sent_at, tracking, open_token, attachments_meta, delivery_status, sender:sender_user_id(avatar)")
      .eq("conversation_id", conversationId).order("sent_at", { ascending: true });
    const avatars = await this._contactAvatarMap();
    return ok((data || []).map((m) => _mapMessage(m, avatars)));
  },

  // Full-mailbox search (subject / sender / preview) across all folders except
  // Trash & Spam — server-side so it isn't limited to the loaded page.
  async searchEmail(query, limit = 60, mailboxId = null) {
    const term = (query || "").trim();
    if (!term) return ok({ threads: [] });
    try {
      // Step 1: matching ids via the SECURITY DEFINER RPC — it uses the trigram
      // indexes (RLS would otherwise block them for ILIKE) and enforces per-user
      // visibility internally.
      const { data: idRows, error: e1 } = await supabase.rpc("pulse_search_conversation_ids", { q: term, lim: limit });
      if (e1) return ok({ threads: [] });
      const ids = (idRows || []).map((r) => r.id);
      if (!ids.length) return ok({ threads: [] });
      // Step 2: load the full rows (with messages/notes) for those ids, scoped
      // to the selected mailbox when one is active.
      let rowQ = supabase.from("pulse_conversation").select(_CONV_SELECT_LIST).in("id", ids);
      if (mailboxId) rowQ = rowQ.eq("channel_account_id", mailboxId);
      const { data } = await rowQ;
      const rows = (data || []).sort((a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0));
      const avatars = await this._contactAvatarMap();
      const threads = rows.map((c) => _thread(c, avatars, false));
      await _enrichGravatars(threads);
      return ok({ threads });
    } catch { return ok({ threads: [] }); }
  },

  // Best-effort unread counts per folder for the rail badges (server-side).
  // Unread inbox conversation count (for the nav badge).
  async getEmailUnreadCount() {
    try {
      const { count } = await supabase.from("pulse_conversation")
        .select("id", { count: "exact", head: true })
        .eq("channel", "email").eq("folder", "inbox").gt("unread_count", 0);
      return ok(count || 0);
    } catch { return ok(0); }
  },

  // Number of WhatsApp chats with unread messages (drives the Chats nav badge).
  async getChatsUnreadCount() {
    try {
      const { count } = await supabase.from("pulse_conversation")
        .select("id", { count: "exact", head: true })
        .eq("channel", "whatsapp").gt("unread_count", 0);
      return ok(count || 0);
    } catch { return ok(0); }
  },

  async _folderCounts(mailboxId = null) {
    const out = { inbox: 0, starred: 0, spam: 0, drafts: 0, sent: 0, archive: 0 };
    try {
      const mk = (f) => {
        let q = supabase.from("pulse_conversation").select("id", { count: "exact", head: true }).eq("channel", "email").gt("unread_count", 0);
        if (mailboxId) q = q.eq("channel_account_id", mailboxId);
        return _applyFolder(q, f);
      };
      const [inbox, starred, spam] = await Promise.all([mk("inbox"), mk("starred"), mk("spam")]);
      out.inbox = inbox.count || 0; out.starred = starred.count || 0; out.spam = spam.count || 0;
    } catch { /* counts are best-effort */ }
    return out;
  },

  // Targeted import of a Gmail query (e.g. is:starred) — bounded, in-process,
  // doesn't disturb the full-backfill cursor. Used by "Sync starred".
  async importStarred(mailboxId) {
    const { data, error } = await supabase.functions.invoke("pulse-gmail-backfill", { body: { mailboxId, query: "is:starred" } });
    if (error) return { data: null, error: (data && data.error) || error.message };
    if (data && data.error) return { data: null, error: data.error };
    return ok(data);
  },

  // Pull existing Trash + Spam (the full backfill historically skipped them).
  async importTrashSpam(mailboxId) {
    const { data, error } = await supabase.functions.invoke("pulse-gmail-backfill", { body: { mailboxId, query: "in:trash OR in:spam" } });
    if (error) return { data: null, error: (data && data.error) || error.message };
    if (data && data.error) return { data: null, error: data.error };
    return ok(data);
  },

  // ==================================================================
  // AVATARS (team → users.avatar; external senders → pulse_email_avatar)
  // ==================================================================
  // email(lowercased) → full avatar URL, for resolving inbound sender photos.
  async _contactAvatarMap() {
    try {
      const { data } = await supabase.from("pulse_email_avatar").select("email, avatar_path");
      const map = {};
      (data || []).forEach((r) => { map[(r.email || "").toLowerCase()] = _avatarUrl(r.avatar_path); });
      return map;
    } catch { return {}; }
  },

  // Public: email(lowercased) → avatar URL map (for recipient contact cards).
  async getContactAvatars() { return ok(await this._contactAvatarMap()); },

  // Upload an image to the shared bucket; returns the stored relative path.
  async uploadAvatar(file) {
    try {
      const { data, error } = await StorageService.upload(file, "avatar");
      if (error) return { data: null, error: error.message };
      return ok(data?.path || null);
    } catch (e) { return { data: null, error: String(e) }; }
  },

  // Set an external sender's photo. Returns the full URL on success.
  async setContactAvatar(email, file) {
    const up = await this.uploadAvatar(file);
    if (up.error) return up;
    const uid = await _uid();
    const { error } = await supabase.from("pulse_email_avatar").upsert({
      email: (email || "").toLowerCase(), avatar_path: up.data, source: "manual", updated_by: uid, updated_at: new Date().toISOString(),
    });
    if (error) return { data: null, error: error.message };
    return ok(_avatarUrl(up.data));
  },

  // Pull profile photos from Google (directory + contacts) via delegation and
  // cache them as sender avatars. Won't overwrite manual uploads (source guard
  // lives server-side). Needs the People API scope added in Workspace admin.
  async syncPeopleAvatars(mailboxId) {
    const { data, error } = await supabase.functions.invoke("pulse-gmail-people", { body: { mailboxId } });
    if (error) return { data: null, error: (data && data.error) || error.message };
    if (data && data.error) return { data: null, error: data.error };
    return ok(data);
  },

  // Set a team member's photo (writes users.avatar). Returns the full URL.
  async setMemberAvatar(userId, file) {
    const up = await this.uploadAvatar(file);
    if (up.error) return up;
    const { error } = await supabase.from("users").update({ avatar: up.data }).eq("id", userId);
    if (error) return { data: null, error: error.message };
    return ok(_avatarUrl(up.data));
  },

  // Gmail label/folder catalog across the user's connected mailboxes (for the rail).
  async getLabels() {
    const { data, error } = await supabase.from("pulse_label").select("gmail_label_id, name, label_type, text_color, bg_color, channel_account_id");
    if (error) return { data: [], error: error.message };
    // Dedupe: system labels by id; user labels by name (same label has a
    // different id in each mailbox). Keep a per-mailbox id map so we can apply
    // the right id to a thread based on its mailbox.
    const seen = {};
    (data || []).forEach((l) => {
      const key = l.label_type === "user" ? "u:" + (l.name || "").toLowerCase() : "s:" + l.gmail_label_id;
      if (!seen[key]) seen[key] = { gmail_label_id: l.gmail_label_id, name: l.name, label_type: l.label_type, text_color: l.text_color, bg_color: l.bg_color, byMailbox: {} };
      seen[key].byMailbox[l.channel_account_id] = l.gmail_label_id;
    });
    return ok(Object.values(seen));
  },

  // Create a Gmail label (in every connected mailbox) and return refreshed list.
  async createLabel(name) {
    const { data, error } = await supabase.functions.invoke("pulse-gmail-create-label", { body: { name } });
    if (error) return { data: null, error: (data && data.error) || error.message };
    if (data && data.error) return { data: null, error: data.error };
    const labels = await this.getLabels();
    return ok({ ...data, labels: labels.data || [] });
  },

  // Apply / remove a label on a thread — resolves the label's id for the thread's
  // mailbox, then goes through the two-way modify (Gmail + our copy).
  async applyLabel(thread, label, add) {
    const id = (label.byMailbox && label.byMailbox[thread.mailboxId]) || label.gmail_label_id;
    const extra = add ? { addLabels: [id] } : { removeLabels: [id] };
    return this.modifyConversation(thread.id, null, extra);
  },

  // The signed-in user's id (for the "Mine" scope filter).
  async getMyUserId() { return ok(await _uid()); },

  // Pull the latest from Gmail right now (incremental history sync) — backs the
  // Refresh button. Returns { ingested } count.
  async syncNow() {
    const { data, error } = await supabase.functions.invoke("pulse-gmail-sync", {});
    if (error) return { data: null, error: (data && data.error) || error.message };
    if (data && data.error) return { data: null, error: data.error };
    return ok(data);
  },

  // ==================================================================
  // DRAFTS (synced to Gmail Drafts)
  // ==================================================================
  // Create/update a Gmail draft. payload: { mailboxId, draftId?, conversationId?,
  // mode, to, cc, bcc, subject, body, html }. Returns { draftId } (Gmail id).
  async saveDraft(payload) {
    const { data, error } = await supabase.functions.invoke("pulse-gmail-draft", { body: { op: "save", ...payload } });
    if (error) return { data: null, error: (data && data.error) || error.message };
    if (data && data.error) return { data: null, error: data.error };
    return ok(data); // { draftId }
  },
  async sendDraft(mailboxId, draftId) {
    const { data, error } = await supabase.functions.invoke("pulse-gmail-draft", { body: { op: "send", mailboxId, draftId } });
    if (error) return { data: null, error: (data && data.error) || error.message };
    if (data && data.error) return { data: null, error: data.error };
    return ok(data);
  },
  async deleteDraft(mailboxId, draftId) {
    const { data, error } = await supabase.functions.invoke("pulse-gmail-draft", { body: { op: "delete", mailboxId, draftId } });
    if (error) return { data: null, error: (data && data.error) || error.message };
    return ok(data);
  },
  _draftRow(d) {
    return {
      id: d.draftId, draftId: d.draftId, mode: d.threadId ? "reply" : "compose",
      to: d.to || "", cc: d.cc || "", bcc: "",
      subject: _decode(d.subject) || "(no subject)", body: d.body || "", html: d.html || null,
      snippet: _decode((d.snippet || d.body || "").replace(/\s+/g, " ").trim().slice(0, 140)),
      threadId: d.threadId || null, mailboxId: d.mailboxId, mailbox: d.mailbox,
      when: d.internalDate ? _when(new Date(Number(d.internalDate)).toISOString()) : "",
    };
  },
  // Every Gmail draft across the mailboxes (Drafts folder source of truth).
  async listDrafts() {
    const { data, error } = await supabase.functions.invoke("pulse-gmail-draft", { body: { op: "list" } });
    if (error) return { data: [], error: (data && data.error) || error.message };
    return ok(((data && data.drafts) || []).map((d) => this._draftRow(d)));
  },
  // Full content of one Gmail draft (for resume).
  async getDraft(mailboxId, draftId) {
    const { data, error } = await supabase.functions.invoke("pulse-gmail-draft", { body: { op: "get", mailboxId, draftId } });
    if (error || !data || !data.draft) return ok(null);
    return ok(this._draftRow(data.draft));
  },
  // The Gmail draft on a given thread (to restore a reply on open). Needs the
  // mailbox + Gmail thread id from the conversation.
  async getThreadDraft(mailboxId, threadId) {
    if (!mailboxId || !threadId) return ok(null);
    const { data, error } = await supabase.functions.invoke("pulse-gmail-draft", { body: { op: "forThread", mailboxId, threadId } });
    if (error || !data || !data.draft) return ok(null);
    return ok(this._draftRow(data.draft));
  },

  // Re-sync the Gmail label catalog (picks up newly-created labels), then return
  // the fresh list. Called by the Email screen on load.
  async refreshLabels() {
    const { error } = await supabase.functions.invoke("pulse-gmail-labels", {});
    if (error) return { data: [], error: error.message };
    return this.getLabels();
  },

  // Two-way action on a thread (read/unread/star/archive/trash/spam/label…) — pushes to Gmail.
  async modifyConversation(conversationId, action, extra = {}) {
    const { data, error } = await supabase.functions.invoke("pulse-gmail-modify", { body: { conversationId, action, ...extra } });
    if (error) return { data: null, error: (data && data.error) || error.message };
    if (data && data.error) return { data: null, error: data.error };
    return ok(data);
  },

  // Assign an unassigned conversation to an owner (+ optional campaign) and route it.
  // Assign owner and/or campaign. Both are optional — you can link only a
  // campaign (no owner) or only an owner. A null campaignId leaves any existing
  // (e.g. auto-detected) campaign untouched rather than clearing it.
  async assignConversation(id, ownerId, campaignId) {
    const patch = { owner_id: ownerId || null, assigned_at: new Date().toISOString(), status: "open" };
    if (campaignId) { patch.campaign_id = campaignId; patch.campaign_ids = [campaignId]; }
    const { error } = await supabase.from("pulse_conversation").update(patch).eq("id", id);
    return { data: null, error: error ? error.message : null };
  },

  // Send an outbound reply — goes through the Gmail edge function, which sends
  // via the mailbox (domain-wide delegation) and records the outbound message.
  // `to` / `cc` address the specific message being replied to (see EmailView
  // replyRecipients). Omit them and the server falls back to the conversation's
  // stored address, which is wrong on multi-party threads.
  async sendEmail(conversationId, { body, html, attachments, to, cc, bcc, replyToExternalId }) {
    const { data, error } = await supabase.functions.invoke("pulse-gmail-send", { body: { conversationId, body, html, attachments, to, cc, bcc, replyToExternalId } });
    if (error) return { data: null, error: (data && data.error) || error.message };
    if (data && data.error) return { data: null, error: data.error };
    return ok(data);
  },

  // Compose & send a brand-new email (creates the conversation server-side).
  async composeEmail({ mailboxId, to, cc, bcc, subject, body, html, campaignId, attachments }) {
    const { data, error } = await supabase.functions.invoke("pulse-gmail-compose", { body: { mailboxId, to, cc, bcc, subject, body, html, campaignId, attachments } });
    if (error) return { data: null, error: (data && data.error) || error.message };
    if (data && data.error) return { data: null, error: data.error };
    return ok(data);
  },

  // Mailboxes the current user may send from (for the Compose "From" picker).
  async getMyMailboxes() {
    const uid = await _uid();
    if (!uid) return ok([]);
    const [{ data: accts }, { data: me }, { data: auth }] = await Promise.all([
      supabase.from("pulse_channel_account").select("id, identifier, label, kind").eq("channel", "email").order("id", { ascending: true }),
      supabase.from("pulse_member").select("is_admin, email_access").eq("user_id", uid).maybeSingle(),
      supabase.auth.getUser(),
    ]);
    // `kind` + `mine` drive the "don't mark someone else's personal mail as read"
    // rule in EmailView. `mine` compares the signed-in user's address to the
    // mailbox address: viewing your OWN inbox in Pulse should behave like any
    // other mail client.
    const myAddr = String(auth?.user?.email || "").toLowerCase();
    const list = (accts || []).map((a) => ({
      id: a.id, address: a.identifier, label: a.label || a.identifier,
      kind: a.kind || "personal",
      mine: String(a.identifier || "").toLowerCase() === myAddr,
    }));
    const broad = me && (me.is_admin || me.email_access === "team" || me.email_access === "all");
    if (broad) return ok(list);
    const { data: acc } = await supabase.from("pulse_mailbox_access").select("channel_account_id").eq("user_id", uid);
    const ids = new Set((acc || []).map((r) => r.channel_account_id));
    return ok(list.filter((m) => ids.has(m.id)));
  },

  // Recipient autocomplete book: everyone we've corresponded with (inbound
  // senders + outbound recipients) plus team members, deduped by email. Loaded
  // once and filtered client-side as the user types in To/Cc/Bcc.
  async getRecipientBook() {
    try {
      const [{ data: convs }, { data: msgs }, { data: members }] = await Promise.all([
        supabase.from("pulse_conversation").select("from_name, from_address").eq("channel", "email").order("last_message_at", { ascending: false }).limit(2000),
        supabase.from("pulse_message").select("to_addresses, cc_addresses").eq("channel", "email").order("sent_at", { ascending: false }).limit(1500),
        supabase.from("pulse_member").select("user:user_id(first_name, last_name, email)"),
      ]);
      const map = new Map();
      const add = (name, raw) => {
        // A single field can hold several comma/semicolon-joined addresses (e.g. a
        // multi-recipient sent conversation's from_address). Split + clean each.
        const nm = (name && !/[,@<>]/.test(name)) ? name.trim() : "";
        String(raw || "").split(/[,;]/).forEach((piece) => {
          const m = /<([^>]+)>/.exec(piece);
          const e = (m ? m[1] : piece).trim().toLowerCase();
          if (!e || !e.includes("@") || /\s/.test(e)) return;
          // Skip VERP / bounce / return-path & tokenised machine addresses (not real
          // contacts) — e.g. "…-ahmer=brandripplr.com@bounce.host" or long hash locals.
          const local = e.split("@")[0];
          if (local.includes("=") || local.length > 40 || /\bbounces?\b|\breturn\b|^srs\d?=/.test(local)) return;
          const cur = map.get(e);
          if (!cur) map.set(e, { name: nm, email: e });
          else if (!cur.name && nm) cur.name = nm;
        });
      };
      (convs || []).forEach((c) => add(c.from_name, c.from_address));
      (msgs || []).forEach((m) => {
        (m.to_addresses || []).forEach((a) => { const p = _parseAddr(a); add(p.name, p.email); });
        (m.cc_addresses || []).forEach((a) => { const p = _parseAddr(a); add(p.name, p.email); });
      });
      (members || []).forEach((m) => { if (m.user?.email) add(_fullName(m.user), m.user.email); });
      return ok([...map.values()]);
    } catch { return ok([]); }
  },

  // Short-lived signed URL for an attachment (by name) — access-checked, lazily
  // fetched from Gmail if not yet cached in Storage.
  async getAttachmentUrl(conversationId, name) {
    const { data, error } = await supabase.functions.invoke("pulse-attachment-url", { body: { conversationId, name } });
    if (error) return { data: null, error: (data && data.error) || error.message };
    if (data && data.error) return { data: null, error: data.error };
    return ok(data?.url || null);
  },

  // Kick off / check a full-mailbox history import.
  async startBackfill(mailboxId) {
    const { data, error } = await supabase.functions.invoke("pulse-gmail-backfill", { body: { mailboxId, start: true } });
    if (error) return { data: null, error: (data && data.error) || error.message };
    return ok(data);
  },
  async getBackfillStatus(mailboxId) {
    const { data, error } = await supabase.from("pulse_channel_account").select("backfill_status, backfill_imported").eq("id", mailboxId).maybeSingle();
    if (error) return { data: null, error: error.message };
    return ok(data);
  },

  async setConversationFlag(id, flagged) {
    const { error } = await supabase.from("pulse_conversation").update({ flagged }).eq("id", id);
    return { data: null, error: error ? error.message : null };
  },
  async archiveConversation(id) {
    const { error } = await supabase.from("pulse_conversation").update({ folder: "archive" }).eq("id", id);
    return { data: null, error: error ? error.message : null };
  },
  async markConversationRead(id) {
    const { error } = await supabase.from("pulse_conversation").update({ unread_count: 0 }).eq("id", id);
    return { data: null, error: error ? error.message : null };
  },
  async addNote(conversationId, body, mentions = []) {
    const uid = await _uid();
    const { error } = await supabase.from("pulse_note").insert({ conversation_id: conversationId, author_user_id: uid, body, mentions });
    return { data: null, error: error ? error.message : null };
  },

  // -- staff gate: is the current user staff? (router already enforces) --
  async amStaff() {
    try {
      const { data: u } = await supabase.auth.getUser();
      if (!u?.user?.id) return { data: false, error: null };
      const { data, error } = await supabase.from("users").select("user_type").eq("id", u.user.id).single();
      if (error) return { data: false, error: error.message };
      return ok(data?.user_type === "staff");
    } catch (e) {
      return { data: false, error: String(e) };
    }
  },

  // -- Calls & meetings: transcripts captured by the internal recorder app --
  async getCalls() {
    const { data, error } = await supabase
      .from("pulse_call")
      .select("id, owner_id, title, status, language, device, contact_name, started_at, ended_at, duration_sec, transcript, segments, summary, created_at, owner:owner_id(email, first_name, last_name, avatar)")
      .order("started_at", { ascending: false });
    if (error) return { data: [], error: error.message };
    return ok((data || []).map(_mapCall));
  },
  async getCall(id) {
    const { data, error } = await supabase
      .from("pulse_call")
      .select("id, owner_id, title, status, language, device, contact_name, started_at, ended_at, duration_sec, transcript, segments, summary, created_at, owner:owner_id(email, first_name, last_name, avatar)")
      .eq("id", id).maybeSingle();
    if (error) return { data: null, error: error.message };
    return ok(data ? _mapCall(data) : null);
  },
  async renameCall(id, title) {
    const { error } = await supabase.from("pulse_call").update({ title }).eq("id", id);
    return { data: null, error: error ? error.message : null };
  },
  async deleteCall(id) {
    const { error } = await supabase.from("pulse_call").delete().eq("id", id);
    return { data: null, error: error ? error.message : null };
  },

  // -- Chats (WhatsApp) — live from pulse_conversation / pulse_message ---------
  async getChats() {
    const { data, error } = await supabase.from("pulse_conversation")
      .select("id, from_name, from_address, external_thread_id, preview, unread_count, last_message_at, wa_window_until, channel_account_id, is_group, wa_invite_link, wa_group_status, wa_group_desc, wa_group_error, summary, summary_at, summary_msg_count, wa_avatar_path, awaiting_muted")
      .eq("channel", "whatsapp")
      // 'ignored' = a group on a personal listener that an admin switched off.
      .or("wa_group_status.is.null,wa_group_status.neq.ignored")
      .order("last_message_at", { ascending: false, nullsFirst: false });
    if (error) return { data: [], error: error.message };
    const chats = (data || []).map(_mapChat);
    // WhatsApp group pictures (copied into our bucket by syncAvatars) → signed URLs.
    try {
      const withPic = (data || []).filter((c) => c.wa_avatar_path);
      if (withPic.length) {
        const { data: signed } = await supabase.storage.from("pulse-wa-media").createSignedUrls(withPic.map((c) => c.wa_avatar_path), 3600);
        const byPath = {};
        (signed || []).forEach((s) => { if (s && s.path) byPath[s.path] = s.signedUrl; });
        withPic.forEach((c) => { const ch = chats.find((x) => x.id === c.id); if (ch && byPath[c.wa_avatar_path]) ch.avatar = byPath[c.wa_avatar_path]; });
      }
    } catch { /* pictures are best-effort */ }
    // Auto-match contact photos from our influencer roster by phone number.
    try {
      const digits = (s) => (s || "").replace(/\D/g, "").slice(-9);
      const phones = chats.map((c) => c.number).filter(Boolean);
      if (phones.length) {
        const { data: rows } = await supabase.rpc("pulse_wa_contact_avatars", { p_phones: phones });
        const byPhone = {};
        (rows || []).forEach((r) => { if (r && r.avatar) byPhone[digits(r.phone)] = _avatarUrl(r.avatar); });
        chats.forEach((c) => { const a = byPhone[digits(c.number)]; if (a && !c.avatar) c.avatar = a; });
      }
    } catch { /* avatar match is best-effort */ }
    return ok(chats);
  },
  async getChatMessages(conversationId) {
    const { data, error } = await supabase.from("pulse_message")
      .select("id, external_message_id, direction, type, body, from_name, from_address, delivery_status, delivery_error, sent_at, starred, forwarded, reactions, reply_to, reply_snippet, reply_dir, media_path, media_mime, media_name, media_duration, location, attachments_meta, transcript, sender:sender_user_id(first_name, last_name)")
      .eq("conversation_id", conversationId).order("sent_at", { ascending: true });
    if (error) return { data: [], error: error.message };
    const mapped = (data || []).map(_mapChatMsg);
    // Resolve short-lived signed URLs for any media in one round-trip.
    const paths = mapped.filter((m) => m.media && m.media.path).map((m) => m.media.path);
    if (paths.length) {
      const { data: signed } = await supabase.storage.from("pulse-wa-media").createSignedUrls(paths, 3600);
      const byPath = {};
      (signed || []).forEach((s) => { if (s && s.path) byPath[s.path] = s.signedUrl; });
      mapped.forEach((m) => { if (m.media && byPath[m.media.path]) m.media.url = byPath[m.media.path]; });
    }
    return ok(mapped);
  },
  async sendWhatsapp(conversationId, text, reply) {
    const body = { conversationId, text };
    if (reply) { body.replyTo = reply.wamid; body.replySnippet = reply.snippet; body.replyDir = reply.dir; }
    const { data, error } = await supabase.functions.invoke("pulse-whatsapp-send", { body });
    if (error) return { data: null, error: error.message };
    return ok(data);
  },
  // React to a message (emoji "" clears). wamid = the message's WhatsApp id.
  async reactWhatsapp(conversationId, wamid, emoji) {
    const { data, error } = await supabase.functions.invoke("pulse-whatsapp-react", { body: { conversationId, messageId: wamid, emoji } });
    if (error) return { data: null, error: error.message };
    return ok(data);
  },
  // Mark the chat's latest inbound message read (blue ticks); typing=true shows "typing…".
  async markWhatsappRead(conversationId, typing = false) {
    const { data, error } = await supabase.functions.invoke("pulse-whatsapp-read", { body: { conversationId, typing } });
    if (error) return { data: null, error: error.message };
    return ok(data);
  },
  // Star / unstar a message locally (staff-visible only).
  async starMessage(id, starred) {
    const { error } = await supabase.from("pulse_message").update({ starred }).eq("id", id);
    return { data: null, error: error ? error.message : null };
  },
  // Delete a message from Pulse (removes it from the team's record).
  async deleteMessage(id) {
    if (typeof id === "string" && id.startsWith("tmp")) return { data: null, error: null };
    const { error } = await supabase.from("pulse_message").delete().eq("id", id);
    return { data: null, error: error ? error.message : null };
  },
  // Upload a file to the media bucket, then send it. `kind`: image|video|audio|document.
  async sendWhatsappMedia(conversationId, file, opts = {}) {
    try {
      const ext = (file.name && file.name.includes(".")) ? file.name.split(".").pop() : (file.type.split("/")[1] || "bin");
      const path = `${conversationId}/out-${Date.now()}.${ext}`;
      const up = await supabase.storage.from("pulse-wa-media").upload(path, file, { contentType: file.type || "application/octet-stream", upsert: true });
      if (up.error) return { data: null, error: up.error.message };
      const body = { conversationId, path, kind: opts.kind, mime: file.type, caption: opts.caption || "", filename: file.name || "", duration: opts.duration || "", voice: !!opts.voice };
      if (opts.reply) { body.replyTo = opts.reply.wamid; body.replySnippet = opts.reply.snippet; body.replyDir = opts.reply.dir; }
      const { data, error } = await supabase.functions.invoke("pulse-whatsapp-media", { body });
      if (error) return { data: null, error: error.message };
      if (data && data.error) return { data: null, error: data.error };
      return ok(data);
    } catch (e) { return { data: null, error: String(e) }; }
  },
  // ---- WhatsApp groups (Groups API, requires an Official Business Account) ----
  // Creation is async: this returns immediately with a 'pending' conversation;
  // Meta's group_lifecycle_update webhook fills in the group id + invite link,
  // which arrives in the SPA over realtime.
  async createWhatsappGroup({ channelAccountId, subject, description }) {
    const { data, error } = await supabase.functions.invoke("pulse-whatsapp-group", {
      body: { action: "create", channelAccountId, subject, description },
    });
    if (error) return { data: null, error: error.message };
    if (data && data.success) return ok(data);
    return { data: null, error: (data && data.error) || "Couldn't create the group" };
  },
  // Pull live group state (subject, description, invite link) back from Meta.
  async refreshWhatsappGroup(conversationId) {
    const { data, error } = await supabase.functions.invoke("pulse-whatsapp-group", {
      body: { action: "refresh", conversationId },
    });
    if (error) return { data: null, error: error.message };
    if (data && data.success) return ok(data.group || {});
    return { data: null, error: (data && data.error) || "Couldn't refresh the group" };
  },
  async deleteWhatsappGroup(conversationId) {
    const { data, error } = await supabase.functions.invoke("pulse-whatsapp-group", {
      body: { action: "delete", conversationId },
    });
    if (error) return { data: null, error: error.message };
    if (data && data.success) return ok(data);
    return { data: null, error: (data && data.error) || "Couldn't delete the group" };
  },
  // Current members of a group thread.
  async getGroupParticipants(conversationId) {
    const [{ data, error }, { data: members }, { data: staff }] = await Promise.all([
      supabase.from("pulse_wa_group_participant")
        .select("wa_id, display_name, is_admin, joined_at, left_at, avatar_path")
        .eq("conversation_id", conversationId).is("left_at", null)
        .order("joined_at", { ascending: true }),
      // "Us" in the roster — same two sources the webhook uses: the staff
      // record's contact number, or a Pulse member phone as an override.
      supabase.from("pulse_member").select("phone, user:user_id(first_name, last_name)").not("phone", "is", null),
      supabase.from("staff").select("contact_number, user:users!staff_user_id_fkey(first_name, last_name)").not("contact_number", "is", null),
    ]);
    if (error) return { data: [], error: error.message };
    const digits = (s) => String(s || "").replace(/\D/g, "");
    const staffByTail = {};
    (staff || []).forEach((s) => { const d = digits(s.contact_number); if (d.length >= 9 && !/^(\d)\1{6,}$/.test(d.slice(-7))) staffByTail[d.slice(-9)] = _fullName(s.user) || d; });
    (members || []).forEach((s) => { const d = digits(s.phone); if (d.length >= 9) staffByTail[d.slice(-9)] = _fullName(s.user) || d; });
    // Our own listener number sits in every group — name it so it isn't a bare +9715….
    const { data: listeners } = await supabase.from("pulse_channel_account").select("identifier, label")
      .eq("channel", "whatsapp").eq("provider", "wasender");
    (listeners || []).forEach((l) => { const d = digits(l.identifier); if (d.length >= 9) staffByTail[d.slice(-9)] = (l.label || "Pulse") + " · listener"; });
    const rows = (data || []).map((p) => {
      const d = digits(p.wa_id);
      const isPhone = d.length >= 9 && d.length <= 15 && d === String(p.wa_id);
      const staffName = isPhone ? staffByTail[d.slice(-9)] : "";
      const name = staffName || p.display_name || (isPhone ? "+" + d : "Unknown");
      return {
        waId: p.wa_id,
        name,
        phone: isPhone ? "+" + d : "",
        isAdmin: !!p.is_admin,
        isStaff: !!staffName,
        initials: mock.initials(name),
        color: mock.colorFor(name),
        joinedAt: p.joined_at,
        avatarPath: p.avatar_path || "",
        avatar: null,
      };
    });
    // Us first, then admins, then the rest alphabetically.
    rows.sort((a, b) => (b.isStaff - a.isStaff) || (b.isAdmin - a.isAdmin) || a.name.localeCompare(b.name));
    // Member pictures (synced copies) → signed URLs.
    try {
      const paths = [...new Set(rows.filter((r) => r.avatarPath).map((r) => r.avatarPath))];
      if (paths.length) {
        const { data: signed } = await supabase.storage.from("pulse-wa-media").createSignedUrls(paths, 3600);
        const byPath = {};
        (signed || []).forEach((s) => { if (s && s.path) byPath[s.path] = s.signedUrl; });
        rows.forEach((r) => { if (byPath[r.avatarPath]) r.avatar = byPath[r.avatarPath]; });
      }
    } catch { /* best effort */ }
    return ok(rows);
  },

  // Staff directory for the "link this number to a colleague" picker.
  async getStaffDirectory() {
    const { data, error } = await supabase.from("staff").select("user_id, user:users!staff_user_id_fkey(first_name, last_name, email)").limit(300);
    if (error) return { data: [], error: error.message };
    const rows = (data || []).map((r) => ({ userId: r.user_id, name: _fullName(r.user) || (r.user && r.user.email) || "?", email: (r.user && r.user.email) || "" }))
      .filter((r) => r.userId).sort((a, b) => a.name.localeCompare(b.name));
    return ok(rows);
  },
  // Link a WhatsApp number to a colleague: writes staff.contact_number, then
  // re-attributes that number's past messages to our side.
  async linkWhatsappNumberToStaff(phone, userId) {
    const d = String(phone || "").replace(/\D/g, "");
    if (!d || !userId) return { data: null, error: "Pick a colleague" };
    return this.wasenderAdmin("linkStaffPhone", { phone: d, userId });
  },

  // Send a location pin. loc = { lat, lng, name?, address? }.
  async sendWhatsappLocation(conversationId, loc, reply) {
    const body = { conversationId, location: loc };
    if (reply) { body.replyTo = reply.wamid; body.replySnippet = reply.snippet; body.replyDir = reply.dir; }
    const { data, error } = await supabase.functions.invoke("pulse-whatsapp-send", { body });
    if (error) return { data: null, error: error.message };
    if (data && data.error) return { data: null, error: data.error };
    return ok(data);
  },
  async sendWhatsappTemplate(conversationId, template) {
    const { data, error } = await supabase.functions.invoke("pulse-whatsapp-send", { body: { conversationId, template } });
    if (error) return { data: null, error: error.message };
    return ok(data);
  },
  async markChatRead(conversationId) {
    const { error } = await supabase.from("pulse_conversation").update({ unread_count: 0 }).eq("id", conversationId);
    return { data: null, error: error ? error.message : null };
  },
  // Forward a message to another chat (window must be open on the target).
  async forwardMessage(targetConversationId, message) {
    const KIND = { image: "image", video: "video", voice: "audio", audio: "audio", doc: "document" };
    if (message.media && message.media.url && KIND[message.type]) {
      try {
        const res = await fetch(message.media.url);
        const blob = await res.blob();
        const file = new File([blob], message.media.name || `forward.${(blob.type.split("/")[1] || "bin")}`, { type: blob.type });
        return this.sendWhatsappMedia(targetConversationId, file, { kind: KIND[message.type], caption: message.text || "" });
      } catch (e) { return { data: null, error: String(e) }; }
    }
    return this.sendWhatsapp(targetConversationId, message.text || "");
  },
  async importWhatsappChats(payload) {
    const { data, error } = await supabase.functions.invoke("pulse-whatsapp-import", { body: payload });
    if (error) {
      // supabase-js collapses every non-2xx into "Edge Function returned a non-2xx status code"
      // and hides the function's own message on error.context. Surface it.
      let msg = error.message || String(error);
      try { const ctx = await error.context?.json?.(); if (ctx?.error) msg = ctx.error; } catch (_e) { /* not JSON */ }
      return { data: null, error: msg };
    }
    return ok(data);
  },
  async getWhatsappTemplates(channelAccountId) {
    const { data, error } = await supabase.functions.invoke("pulse-whatsapp-templates", { body: { channelAccountId } });
    if (error) return { data: { templates: [], error: error.message }, error: null };
    return ok(data);
  },
  // Start a new WhatsApp chat with an approved template. Returns { id, conversationId }.
  async startWhatsappChat({ to, name, channelAccountId, template }) {
    const { data, error } = await supabase.functions.invoke("pulse-whatsapp-send", { body: { to, name, channelAccountId, template } });
    if (error) return { data: null, error: error.message };
    if (data && data.error) return { data: null, error: data.error };
    return ok(data);
  },
  // Create a message template on Meta (goes to review). Returns { success, id, status, name }.
  async createWhatsappTemplate(payload) {
    const { data, error } = await supabase.functions.invoke("pulse-whatsapp-create-template", { body: payload });
    if (error) return { data: null, error: error.message };
    if (data && !data.success) return { data: null, error: data.error || "Create template failed" };
    return ok(data);
  },
};
